/**
 * Copyright (c) 2025 Kalpit Patel
 *
 * Sole Developer and Owner:
 * Kalpit Patel is the sole developer and creator of this entire application.
 * No other individual or entity has contributed to the development of this software.
 * All rights reserved.
 *
 * This file is part of AksharJobs application.
 */

import React, { useEffect, useState, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faMapMarkerAlt, faEdit, faSave, faTimes, faUser, faBriefcase,
  faCode, faBuilding, faGraduationCap, faCertificate, faLanguage,
  faLink, faCheckCircle, faPassport, faMapMarked, faBullseye,
  faLightbulb, faSlidersH, faProjectDiagram, faUsers, faInfoCircle,
  faUserCheck, faGlobe, faPlus, faTrash, faAward, faRocket, faBars, faArrowUp, faExternalLinkAlt, faCamera, faSearch
} from '@fortawesome/free-solid-svg-icons';
import { faLinkedin, faGithub, faTwitter } from '@fortawesome/free-brands-svg-icons';
import axios from 'axios';
import { buildApiUrl } from '../config/api';
import { resolveDownloadFilename } from '../utils/downloadFilename';
import { useAuth } from '../context/AuthContext';
import ThemedLoadingSpinner from '../components/ThemedLoadingSpinner';
import GoogleMapEmbed from '../components/GoogleMapEmbed';
import AccountDeletionModal from '../components/AccountDeletionModal';
import ResumeUpload from '../components/ResumeUpload';
import globalSkillsData from '../data/global_skills.json';
import { softSkillsSuggestions } from '../data/formOptions';
import { ASSOCIATIONS, COMMUNITIES, ALL_OPTIONS, COUNTRIES as COMMUNITY_COUNTRIES } from '../data/communitiesAndAssociations';
import '../styles/InternMyProfileMobile.css';

const MOBILE_HEADER_HEIGHT = 56;

const NGOS = [
  "Amref Health Africa",
  "African Wildlife Foundation",
  "World Vision Kenya",
  "Oxfam Kenya",
  "Mercy Corps Kenya",
  "Save the Children Kenya",
  "CARE Kenya",
  "Plan International Kenya",
  "Kenya Red Cross Society",
  "Action Against Hunger Kenya",
  "Catholic Relief Services Kenya",
  "International Rescue Committee Kenya",
  "Doctors Without Borders (MSF) Kenya",
  "African Population and Health Research Center (APHRC)",
  "Kenya Human Rights Commission",
  "Federation of Women Lawyers – Kenya (FIDA Kenya)",
  "Transparency International Kenya",
  "Umande Trust",
  "Carolina for Kibera (CFK Africa)",
  "Horn of Africa Development Initiative (HODI)",
  "Green Belt Movement",
  "WildlifeDirect",
  "Handicap International Kenya",
  "Water.org Kenya",
  "Living Goods Kenya",
  "Elite Ability Foundation Africa (ELAFA)"
];

/** Normalize technical/soft skills from API (list, JSON string, or legacy comma-separated) */
function normalizeSkillStringArray(val) {
  if (val == null) return [];
  if (Array.isArray(val)) {
    return val.map((x) => (typeof x === 'string' ? x : String(x))).map((s) => s.trim()).filter(Boolean);
  }
  if (typeof val === 'string') {
    const t = val.trim();
    if (!t) return [];
    if (t.startsWith('[')) {
      try {
        const parsed = JSON.parse(t);
        return normalizeSkillStringArray(parsed);
      } catch {
        return [];
      }
    }
    return t.split(',').map((s) => s.trim()).filter(Boolean);
  }
  return [];
}

// NGO Selector Component with Search and Manual Entry
const NGOSelector = ({ value = '', onChange }) => {
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [manualInput, setManualInput] = useState('');
  const [showManualForm, setShowManualForm] = useState(false);
  const dropdownRef = useRef(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSelectNGO = (ngo) => {
    onChange(ngo);
    setDropdownOpen(false);
    setSearchQuery('');
  };

  const handleAddManual = () => {
    if (manualInput.trim()) {
      onChange(manualInput.trim());
      setManualInput('');
      setShowManualForm(false);
    }
  };

  const handleClear = () => {
    onChange('');
    setSearchQuery('');
  };

  // Filter NGOs based on search query
  const filteredNGOS = NGOS.filter(ngo =>
    ngo.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <div style={{ width: '100%', position: 'relative' }}>
      {/* Dropdown Selector with Search */}
      <div style={{ position: 'relative', marginBottom: '10px' }} ref={dropdownRef}>
        <div style={{ display: 'flex', gap: '8px' }}>
          <div style={{ flex: 1, position: 'relative' }}>
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                border: '1px solid #ddd',
                borderRadius: '6px',
                background: 'white',
                cursor: 'pointer'
              }}
              onClick={() => setDropdownOpen(!dropdownOpen)}
            >
              <div style={{ padding: '8px 12px', color: '#666' }}>
                <FontAwesomeIcon icon={faSearch} />
              </div>
              <input
                type="text"
                placeholder="Search or type NGO name..."
                value={searchQuery}
                onChange={(e) => {
                  setSearchQuery(e.target.value);
                  setDropdownOpen(true);
                }}
                onFocus={() => setDropdownOpen(true)}
                style={{
                  flex: 1,
                  padding: '10px',
                  border: 'none',
                  outline: 'none',
                  fontSize: '14px',
                  borderRadius: '6px'
                }}
              />
            </div>

            {/* Custom Dropdown */}
            {dropdownOpen && (
              <div style={{
                position: 'absolute',
                bottom: '100%',
                left: 0,
                right: 0,
                background: 'white',
                border: '1px solid #ddd',
                borderRadius: '6px',
                marginBottom: '4px',
                maxHeight: '300px',
                overflowY: 'auto',
                zIndex: 9999,
                boxShadow: '0 4px 12px rgba(0,0,0,0.15)'
              }}>
                {filteredNGOS.length === 0 && !searchQuery ? (
                  <div style={{ padding: '20px', textAlign: 'center', color: '#999' }}>
                    No NGOs found
                  </div>
                ) : filteredNGOS.length === 0 && searchQuery ? (
                  <div style={{ padding: '20px', textAlign: 'center', color: '#999' }}>
                    No matching NGOs found. Use "Add Manually" to add "{searchQuery}"
                  </div>
                ) : (
                  filteredNGOS.map((ngo, idx) => (
                    <div
                      key={idx}
                      onClick={() => handleSelectNGO(ngo)}
                      style={{
                        padding: '12px 15px',
                        cursor: 'pointer',
                        borderBottom: idx < filteredNGOS.length - 1 ? '1px solid #f0f0f0' : 'none',
                        transition: 'background 0.2s'
                      }}
                      onMouseEnter={(e) => e.target.style.background = '#f8f9fa'}
                      onMouseLeave={(e) => e.target.style.background = 'white'}
                    >
                      {ngo}
                    </div>
                  ))
                )}
              </div>
            )}
          </div>
          <button
            type="button"
            onClick={() => {
              setShowManualForm(!showManualForm);
              setDropdownOpen(false);
            }}
            style={{
              padding: '10px 15px',
              background: '#3498db',
              color: 'white',
              border: 'none',
              borderRadius: '6px',
              cursor: 'pointer',
              fontSize: '14px',
              whiteSpace: 'nowrap'
            }}
          >
            <FontAwesomeIcon icon={faPlus} /> Add Manually
          </button>
        </div>
      </div>

      {/* Manual Entry Form */}
      {showManualForm && (
        <div style={{
          padding: '15px',
          border: '1px solid #ddd',
          borderRadius: '6px',
          marginBottom: '10px',
          background: '#f9f9f9'
        }}>
          <div style={{ marginBottom: '10px' }}>
            <label style={{ display: 'block', marginBottom: '5px', fontWeight: '500' }}>
              NGO Name <span style={{ color: 'red' }}>*</span>
            </label>
            <input
              type="text"
              value={manualInput}
              onChange={(e) => setManualInput(e.target.value)}
              placeholder="Enter NGO name"
              style={{
                width: '100%',
                padding: '8px',
                border: '1px solid #ddd',
                borderRadius: '4px'
              }}
              onKeyPress={(e) => {
                if (e.key === 'Enter' && manualInput.trim()) {
                  handleAddManual();
                }
              }}
            />
          </div>
          <div style={{ display: 'flex', gap: '8px' }}>
            <button
              type="button"
              onClick={handleAddManual}
              disabled={!manualInput.trim()}
              style={{
                padding: '8px 15px',
                background: manualInput.trim() ? '#27ae60' : '#bdc3c7',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: manualInput.trim() ? 'pointer' : 'not-allowed'
              }}
            >
              Add
            </button>
            <button
              type="button"
              onClick={() => {
                setShowManualForm(false);
                setManualInput('');
              }}
              style={{
                padding: '8px 15px',
                background: '#95a5a6',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer'
              }}
            >
              Cancel
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

// Community/Association Selector Component (reusable)
const CommunityAssociationSelector = ({ value = [], onChange }) => {
  const [selectedItems, setSelectedItems] = useState(value);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [manualInput, setManualInput] = useState({ name: '', type: 'community', country: '' });
  const [showManualForm, setShowManualForm] = useState(false);
  const dropdownRef = useRef(null);

  useEffect(() => {
    setSelectedItems(value);
  }, [value]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleAddFromDropdown = (option) => {
    const newItem = {
      name: option.name,
      type: option.type,
      country: ''
    };
    if (!selectedItems.some(item => item.name === option.name && item.type === option.type)) {
      const updated = [...selectedItems, newItem];
      setSelectedItems(updated);
      onChange(updated);
    }
    setDropdownOpen(false);
    setSearchQuery('');
  };

  const handleAddManual = () => {
    if (manualInput.name.trim()) {
      const newItem = {
        name: manualInput.name.trim(),
        type: manualInput.type,
        country: manualInput.country
      };
      if (!selectedItems.some(item => item.name === newItem.name && item.type === newItem.type)) {
        const updated = [...selectedItems, newItem];
        setSelectedItems(updated);
        onChange(updated);
        setManualInput({ name: '', type: 'community', country: '' });
        setShowManualForm(false);
      }
    }
  };

  const handleRemove = (index) => {
    const updated = selectedItems.filter((_, i) => i !== index);
    setSelectedItems(updated);
    onChange(updated);
  };

  const handleUpdateCountry = (index, country) => {
    const updated = selectedItems.map((item, i) => 
      i === index ? { ...item, country } : item
    );
    setSelectedItems(updated);
    onChange(updated);
  };

  // Filter and group options
  const availableOptions = ALL_OPTIONS.filter(opt => 
    !selectedItems.some(item => item.name === opt.name && item.type === opt.type)
  );

  const filteredOptions = availableOptions.filter(opt =>
    opt.name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const associations = filteredOptions.filter(opt => opt.type === 'association');
  const communities = filteredOptions.filter(opt => opt.type === 'community');

  return (
    <div style={{ width: '100%' }}>
      {/* Dropdown Selector with Search */}
      <div style={{ position: 'relative', marginBottom: '10px' }} ref={dropdownRef}>
        <div style={{ display: 'flex', gap: '8px' }}>
          <div style={{ flex: 1, position: 'relative' }}>
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                border: '1px solid #ddd',
                borderRadius: '6px',
                background: 'white',
                cursor: 'pointer'
              }}
              onClick={() => setDropdownOpen(!dropdownOpen)}
            >
              <div style={{ padding: '8px 12px', color: '#666' }}>
                <FontAwesomeIcon icon={faSearch} />
              </div>
              <input
                type="text"
                placeholder="Search communities/associations..."
                value={searchQuery}
                onChange={(e) => {
                  setSearchQuery(e.target.value);
                  setDropdownOpen(true);
                }}
                onFocus={() => setDropdownOpen(true)}
                style={{
                  flex: 1,
                  padding: '10px',
                  border: 'none',
                  outline: 'none',
                  fontSize: '14px',
                  borderRadius: '6px'
                }}
              />
            </div>

            {/* Custom Dropdown */}
            {dropdownOpen && (
              <div style={{
                position: 'absolute',
                top: '100%',
                left: 0,
                right: 0,
                background: 'white',
                border: '1px solid #ddd',
                borderRadius: '6px',
                marginTop: '4px',
                maxHeight: '400px',
                overflowY: 'auto',
                zIndex: 1000,
                boxShadow: '0 4px 12px rgba(0,0,0,0.15)'
              }}>
                {filteredOptions.length === 0 ? (
                  <div style={{ padding: '20px', textAlign: 'center', color: '#999' }}>
                    No results found
                  </div>
                ) : (
                  <>
                    {/* Associations Section */}
                    {associations.length > 0 && (
                      <div>
                        <div style={{
                          padding: '10px 15px',
                          background: '#f8f9fa',
                          borderBottom: '1px solid #e9ecef',
                          fontWeight: '600',
                          fontSize: '13px',
                          color: '#495057',
                          textTransform: 'uppercase',
                          letterSpacing: '0.5px'
                        }}>
                          Associations
                        </div>
                        {associations.map((opt, idx) => (
                          <div
                            key={`assoc-${idx}`}
                            onClick={() => handleAddFromDropdown(opt)}
                            style={{
                              padding: '12px 15px',
                              cursor: 'pointer',
                              borderBottom: '1px solid #f0f0f0',
                              transition: 'background 0.2s'
                            }}
                            onMouseEnter={(e) => e.target.style.background = '#f8f9fa'}
                            onMouseLeave={(e) => e.target.style.background = 'white'}
                          >
                            {opt.name}
                          </div>
                        ))}
                      </div>
                    )}

                    {/* Communities Section */}
                    {communities.length > 0 && (
                      <div>
                        <div style={{
                          padding: '10px 15px',
                          background: '#f8f9fa',
                          borderTop: associations.length > 0 ? '2px solid #e9ecef' : 'none',
                          borderBottom: '1px solid #e9ecef',
                          fontWeight: '600',
                          fontSize: '13px',
                          color: '#495057',
                          textTransform: 'uppercase',
                          letterSpacing: '0.5px'
                        }}>
                          Communities
                        </div>
                        {communities.map((opt, idx) => (
                          <div
                            key={`comm-${idx}`}
                            onClick={() => handleAddFromDropdown(opt)}
                            style={{
                              padding: '12px 15px',
                              cursor: 'pointer',
                              borderBottom: idx < communities.length - 1 ? '1px solid #f0f0f0' : 'none',
                              transition: 'background 0.2s'
                            }}
                            onMouseEnter={(e) => e.target.style.background = '#f8f9fa'}
                            onMouseLeave={(e) => e.target.style.background = 'white'}
                          >
                            {opt.name}
                          </div>
                        ))}
                      </div>
                    )}
                  </>
                )}
              </div>
            )}
          </div>
          <button
            type="button"
            onClick={() => setShowManualForm(!showManualForm)}
            style={{
              padding: '10px 15px',
              background: '#3498db',
              color: 'white',
              border: 'none',
              borderRadius: '6px',
              cursor: 'pointer',
              fontSize: '14px',
              whiteSpace: 'nowrap'
            }}
          >
            <FontAwesomeIcon icon={faPlus} /> Add Manually
          </button>
        </div>
      </div>

      {showManualForm && (
        <div style={{
          padding: '15px',
          border: '1px solid #ddd',
          borderRadius: '6px',
          marginBottom: '10px',
          background: '#f9f9f9'
        }}>
          <div style={{ marginBottom: '10px' }}>
            <label style={{ display: 'block', marginBottom: '5px', fontWeight: '500' }}>
              Name <span style={{ color: 'red' }}>*</span>
            </label>
            <input
              type="text"
              value={manualInput.name}
              onChange={(e) => setManualInput({ ...manualInput, name: e.target.value })}
              placeholder="Enter community/association name"
              style={{
                width: '100%',
                padding: '8px',
                border: '1px solid #ddd',
                borderRadius: '4px'
              }}
            />
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px', marginBottom: '10px' }}>
            <div>
              <label style={{ display: 'block', marginBottom: '5px', fontWeight: '500' }}>Type</label>
              <select
                value={manualInput.type}
                onChange={(e) => setManualInput({ ...manualInput, type: e.target.value })}
                style={{
                  width: '100%',
                  padding: '8px',
                  border: '1px solid #ddd',
                  borderRadius: '4px'
                }}
              >
                <option value="community">Community</option>
                <option value="association">Association</option>
              </select>
            </div>
            <div>
              <label style={{ display: 'block', marginBottom: '5px', fontWeight: '500' }}>Country</label>
              <select
                value={manualInput.country}
                onChange={(e) => setManualInput({ ...manualInput, country: e.target.value })}
                style={{
                  width: '100%',
                  padding: '8px',
                  border: '1px solid #ddd',
                  borderRadius: '4px'
                }}
              >
                <option value="">Select country</option>
                {COMMUNITY_COUNTRIES.map(country => (
                  <option key={country} value={country}>{country}</option>
                ))}
              </select>
            </div>
          </div>
          <div style={{ display: 'flex', gap: '8px' }}>
            <button
              type="button"
              onClick={handleAddManual}
              disabled={!manualInput.name.trim()}
              style={{
                padding: '8px 15px',
                background: manualInput.name.trim() ? '#27ae60' : '#bdc3c7',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: manualInput.name.trim() ? 'pointer' : 'not-allowed'
              }}
            >
              Add
            </button>
            <button
              type="button"
              onClick={() => {
                setShowManualForm(false);
                setManualInput({ name: '', type: 'community', country: '' });
              }}
              style={{
                padding: '8px 15px',
                background: '#95a5a6',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer'
              }}
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {selectedItems.length > 0 && (
        <div style={{ marginTop: '15px' }}>
          <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>
            Selected Communities/Associations:
          </label>
          {selectedItems.map((item, index) => (
            <div
              key={index}
              style={{
                padding: '12px',
                border: '1px solid #ddd',
                borderRadius: '6px',
                marginBottom: '8px',
                background: 'white',
                display: 'flex',
                flexDirection: 'column',
                gap: '8px'
              }}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div>
                  <strong>{item.name}</strong>
                  <span style={{
                    marginLeft: '10px',
                    padding: '2px 8px',
                    background: item.type === 'association' ? '#e3f2fd' : '#f3e5f5',
                    color: item.type === 'association' ? '#1976d2' : '#7b1fa2',
                    borderRadius: '12px',
                    fontSize: '12px',
                    fontWeight: '500'
                  }}>
                    {item.type === 'association' ? 'Association' : 'Community'}
                  </span>
                </div>
                <button
                  type="button"
                  onClick={() => handleRemove(index)}
                  style={{
                    background: 'none',
                    border: 'none',
                    color: '#e74c3c',
                    cursor: 'pointer',
                    fontSize: '16px',
                    padding: '4px 8px'
                  }}
                >
                  <FontAwesomeIcon icon={faTimes} />
                </button>
              </div>
              <div>
                <label style={{ fontSize: '12px', color: '#666', display: 'block', marginBottom: '4px' }}>
                  Country/Location:
                </label>
                <select
                  value={item.country || ''}
                  onChange={(e) => handleUpdateCountry(index, e.target.value)}
                  style={{
                    width: '100%',
                    padding: '6px',
                    border: '1px solid #ddd',
                    borderRadius: '4px',
                    fontSize: '13px'
                  }}
                >
                  <option value="">Select country</option>
                  {COMMUNITY_COUNTRIES.map(country => (
                    <option key={country} value={country}>{country}</option>
                  ))}
                </select>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const normalizeVolunteeringValue = (value) => {
  if (value === true || value === 'true' || value === 'yes') {
    return 'yes';
  }
  if (value === false || value === 'false' || value === 'no') {
    return 'no';
  }
  return value || '';
};

const InternMyProfile = () => {
  const { user: currentUser, logout } = useAuth();
  const navigate = useNavigate();
  const { id: viewUserId } = useParams();
  const queryClient = useQueryClient(); // React Query client for cache invalidation
  const [viewingUser, setViewingUser] = useState(null);
  const isViewingOtherProfile = !!viewUserId && viewUserId !== currentUser?._id && viewUserId !== currentUser?.id;
  const viewerRoleRaw = (currentUser?.role || currentUser?.userType || '').toLowerCase();
  const viewerRole = viewerRoleRaw.trim();
  const user = isViewingOtherProfile ? viewingUser : currentUser;
  const isAdminViewer = ['admin', 'super_admin', 'superadmin', 'administrator'].includes(viewerRole);
  const isEmployerOrRecruiterViewer = ['employer', 'recruiter'].includes(viewerRole);
  const isInternViewer = ['intern'].includes(viewerRole);
  const canEditProfile = !isViewingOtherProfile && isInternViewer;
  const [editMode, setEditMode] = useState(false);
  const [editingSections, setEditingSections] = useState({});
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState('');
  const [profileCompletionPercentage, setProfileCompletionPercentage] = useState(0);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [isMobileView, setIsMobileView] = useState(() => {
    if (typeof window === 'undefined') return false;
    return window.innerWidth <= 768;
  });
  const [mobileSidebarOpen, setMobileSidebarOpen] = useState(() => {
    if (typeof window === 'undefined') return true;
    return window.innerWidth > 768;
  });
  const [showScrollTop, setShowScrollTop] = useState(false);
  const [showResumeUpload, setShowResumeUpload] = useState(false);

  const getPrimaryLocationDisplay = () => {
    if (profileForm.location && profileForm.location.trim()) {
      return profileForm.location;
    }
    const fallback = `${profileForm.currentCity || ''}, ${profileForm.residentCountry || ''}`
      .replace(/^,\s*|,\s*$/g, '')
      .trim();
    return fallback || '—';
  };

  const openLocationInMaps = () => {
    const displayLocation = getPrimaryLocationDisplay();
    const lat = profileForm.latitude ? parseFloat(profileForm.latitude) : null;
    const lng = profileForm.longitude ? parseFloat(profileForm.longitude) : null;
    let mapsUrl = 'https://www.google.com/maps/search/?api=1';

    if (!Number.isNaN(lat) && !Number.isNaN(lng) && lat !== null && lng !== null) {
      mapsUrl += `&query=${encodeURIComponent(`${lat},${lng}`)}`;
    } else if (displayLocation && displayLocation !== '—') {
      mapsUrl += `&query=${encodeURIComponent(displayLocation)}`;
    } else {
      return;
    }

    window.open(mapsUrl, '_blank', 'noopener,noreferrer');
  };
  
  // State for all form fields
  const [profileForm, setProfileForm] = useState({
    firstName: '',
    lastName: '',
    middleName: '',
    email: '',
    phone: '',
    altPhone: '',
    ngo: '',
    hasDisability: 'no',
    disabilityDescription: '',
    communitiesAssociations: [],
    dateOfBirth: '',
    gender: '',
    genderOther: '',
    nationality: '',
    residentCountry: '',
    currentCity: '',
    postalCode: '',
    address: '',
    latitude: '',
    longitude: '',
    validDocs: '',
    profilePhotoPath: '',
    preferredLocation1: '',
    preferredLocation2: '',
    preferredLocation3: '',
    willingToRelocate: '',
    internshipMode: '',
    academicLevel: '',
    objective: '',
    industryInterest: '',
    industryInterestOther: '',
    preferredRole: '',
    internshipDuration: '',
    internshipDurationOther: '',
    availability: '',
    availabilityOther: '',
    internshipStartMonthYear: '',
    expectedStipend: '',
    currencyPreference: 'USD',
    currencyPreferenceOther: '',
    unpaidWilling: '',
    academicCredit: '',
    hobbies: '',
    whyInternship: '',
    additionalComments: '',
    participateInVolunteering: '',
    volunteeringDetails: '',
    location: ''
  });

  const mapQuery = (() => {
    const parts = [
      profileForm.address,
      profileForm.location,
      profileForm.currentCity,
      profileForm.state || profileForm.stateProvince || '',
      profileForm.residentCountry,
      profileForm.postalCode,
    ]
      .map(value => (value || '').toString().trim())
      .filter(Boolean);

    if (parts.length > 0) {
      const uniqueParts = [];
      parts.forEach(part => {
        if (!uniqueParts.includes(part)) {
          uniqueParts.push(part);
        }
      });
      return uniqueParts.join(', ');
    }

    const fallback = getPrimaryLocationDisplay();
    return fallback === '—' ? '' : fallback;
  })();
  
  const [educationEntries, setEducationEntries] = useState([]);
  const [experienceEntries, setExperienceEntries] = useState([]);
  const [projectEntries, setProjectEntries] = useState([]);
  const [activityEntries, setActivityEntries] = useState([]);
  const [certificationEntries, setCertificationEntries] = useState([]);
  const [referenceEntries, setReferenceEntries] = useState([]);
  const [technicalSkills, setTechnicalSkills] = useState([]);
  const [softSkills, setSoftSkills] = useState([]);
  const [technicalSkillInput, setTechnicalSkillInput] = useState('');
  const [softSkillInput, setSoftSkillInput] = useState('');
  const [languages, setLanguages] = useState([]);
  const [careerInterests, setCareerInterests] = useState([]);
  const [careerInterestInput, setCareerInterestInput] = useState('');
  const [professionalLinks, setProfessionalLinks] = useState([]);
  const [linkInput, setLinkInput] = useState({ type: '', url: '' });
  const [profilePhotoFile, setProfilePhotoFile] = useState(null);
  const [profilePhotoPreview, setProfilePhotoPreview] = useState(null);
  const profilePhotoInputRef = useRef(null);

  useEffect(() => {
    const handleResize = () => {
      const mobile = window.innerWidth <= 768;
      setIsMobileView(mobile);
    };

    if (typeof window !== 'undefined') {
      handleResize();
      window.addEventListener('resize', handleResize);
    }

    return () => {
      if (typeof window !== 'undefined') {
        window.removeEventListener('resize', handleResize);
      }
    };
  }, []);

  useEffect(() => {
    if (isMobileView) {
      setMobileSidebarOpen(false);
    } else {
      setMobileSidebarOpen(true);
    }
  }, [isMobileView]);

  useEffect(() => {
    const handleScroll = () => {
      setShowScrollTop(window.scrollY > 300);
    };

    if (typeof window !== 'undefined') {
      window.addEventListener('scroll', handleScroll, { passive: true });
    }

    return () => {
      if (typeof window !== 'undefined') {
        window.removeEventListener('scroll', handleScroll);
      }
    };
  }, []);

  // Style objects matching job seeker profile
  const containerStyle = { 
    padding: '30px 30px 60px 30px', 
    maxWidth: '100%', 
    width: '100%', 
    margin: '0',
    minHeight: '100vh',
    boxSizing: 'border-box'
  };
  
  const elevatedCardStyle = { 
    border: '1px solid #edf2f7', 
    borderRadius: isMobileView ? '12px' : '16px', 
    boxShadow: '0 10px 30px rgba(17, 24, 39, 0.06)', 
    background: '#fff', 
    marginBottom: isMobileView ? '12px' : '20px',
    overflow: 'hidden',
    padding: isMobileView ? '14px' : '25px'
  };
  
  const profileHeaderStyle = { 
    background: 'linear-gradient(135deg, #f97316 0%, #0d9488 100%)', 
    color: '#fff', 
    padding: isMobileView ? '16px' : '18px 24px', 
    borderRadius: isMobileView ? '12px' : '16px', 
    marginBottom: isMobileView ? '12px' : '16px', 
    position: 'relative',
    overflow: 'hidden',
    minHeight: isMobileView ? 'auto' : '120px',
    boxShadow: '0 10px 30px rgba(102, 126, 234, 0.25)'
  };

  const headerPatternStyle = {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    opacity: 0.12,
    zIndex: 1,
    backgroundImage: `
      radial-gradient(circle at 20% 50%, rgba(255,255,255,0.25) 0%, transparent 55%),
      radial-gradient(circle at 80% 80%, rgba(255,255,255,0.2) 0%, transparent 55%)
    `
  };
  
  const profileAvatarStyle = { 
    width: '76px', 
    height: '76px', 
    borderRadius: '50%', 
    background: 'rgba(255,255,255,0.2)', 
    display: 'flex', 
    alignItems: 'center', 
    justifyContent: 'center', 
    fontSize: '30px', 
    fontWeight: '700', 
    color: '#ffffff',
    boxShadow: '0 6px 18px rgba(0, 0, 0, 0.25)',
    border: '3px solid rgba(255, 255, 255, 0.4)',
    backdropFilter: 'blur(10px)',
    zIndex: 2
  };
  
  const gridStyle = { 
    display: 'grid', 
    gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', 
    gap: '20px', 
    marginBottom: '20px',
    width: '100%'
  };

  const responsiveGridCols = (desktopCols) => (isMobileView ? '1fr' : desktopCols);

  const formGridStyle = (desktopCols, extra = {}) => ({
    ...gridStyle,
    ...extra,
    gap: isMobileView ? '14px' : (extra.gap ?? gridStyle.gap),
    gridTemplateColumns: responsiveGridCols(desktopCols)
  });
  
  const formInputBase = { 
    width: '100%', 
    padding: '10px 15px', 
    border: '2px solid #e0e0e0', 
    borderRadius: '8px', 
    fontSize: '14px', 
    transition: 'border 0.3s, box-shadow 0.3s'
  };

  const viewModeField = !editMode ? { background: '#f7f9fc', borderColor: '#e6eaf1', color: '#333' } : {};

  // Headers for API calls
  const authHeaders = () => ({
    'Authorization': `Bearer ${localStorage.getItem('token')}`,
    'Content-Type': 'application/json'
  });

  // Load profile data
  // Fetch another user's profile when viewing
  useEffect(() => {
    const loadOtherUserProfile = async () => {
      if (!viewUserId || !isViewingOtherProfile) return;
      
      try {
        setLoading(true);
        const response = await axios.get(
          buildApiUrl(`/api/users/profile/${viewUserId}`),
          { headers: authHeaders() }
        );
        
        const p = response.data || {};
        setViewingUser(p);
        
        // Handle location - convert object to string if needed
        let locationString = '';
        if (typeof p.location === 'string') {
          locationString = p.location;
        } else if (p.location && typeof p.location === 'object') {
          // If location is an object, convert it to a string
          const loc = p.location;
          locationString = `${loc.city || loc.currentCity || ''}, ${loc.country || loc.residentCountry || ''}`.replace(/^,\s*|,\s*$/g, '').trim() || 
                         loc.address || 
                         `${p.currentCity || ''}, ${p.residentCountry || ''}`.replace(/^,\s*|,\s*$/g, '').trim() || '';
        } else {
          locationString = `${p.currentCity || ''}, ${p.residentCountry || ''}`.replace(/^,\s*|,\s*$/g, '').trim();
        }
        
        setProfileForm({
          firstName: p.firstName || '',
          lastName: p.lastName || '',
          middleName: p.middleName || '',
          email: p.email || '',
          phone: p.phone || '',
          altPhone: p.altPhone || '',
          ngo: p.ngo || '',
          hasDisability: p.hasDisability || 'no',
          disabilityDescription: p.disabilityDescription || '',
          communitiesAssociations: p.communitiesAssociations || [],
          dateOfBirth: p.dateOfBirth || '',
          gender: p.gender || '',
          genderOther: p.genderOther || '',
          nationality: p.nationality || '',
          residentCountry: p.residentCountry || '',
          currentCity: p.currentCity || '',
          postalCode: p.postalCode || '',
          address: p.address || '',
          latitude: p.latitude || '',
          longitude: p.longitude || '',
          validDocs: p.validDocs || '',
          preferredLocation1: p.preferredLocation1 || '',
          preferredLocation2: p.preferredLocation2 || '',
          preferredLocation3: p.preferredLocation3 || '',
          willingToRelocate: p.willingToRelocate || '',
          internshipMode: p.internshipMode || '',
          academicLevel: p.academicLevel || '',
          objective: p.objective || '',
          industryInterest: p.industryInterest || '',
          industryInterestOther: p.industryInterestOther || '',
          preferredRole: p.preferredRole || '',
          internshipDuration: p.internshipDuration || '',
          internshipDurationOther: p.internshipDurationOther || '',
          availability: p.availability || '',
          availabilityOther: p.availabilityOther || '',
          internshipStartMonthYear: p.internshipStartMonthYear || p.internshipTiming || '',
          expectedStipend: p.expectedStipend || '',
          currencyPreference: p.currencyPreference || 'USD',
          currencyPreferenceOther: p.currencyPreferenceOther || '',
          unpaidWilling: p.unpaidWilling || '',
          academicCredit: p.academicCredit || '',
          hobbies: p.hobbies || '',
          whyInternship: p.whyInternship || '',
          additionalComments: p.additionalComments || '',
          participateInVolunteering: normalizeVolunteeringValue(p.participateInVolunteering),
          participateInVolunteering: normalizeVolunteeringValue(p.participateInVolunteering),
        participateInVolunteering: normalizeVolunteeringValue(p.participateInVolunteering),
          volunteeringDetails: p.volunteeringDetails || '',
          skills: Array.isArray(p.skills) ? p.skills.join(', ') : (p.skills || ''),
          tools: Array.isArray(p.tools) ? p.tools.join(', ') : (p.tools || ''),
          location: locationString
        });
        
        // Set array entries
        setEducationEntries(Array.isArray(p.educationEntries) ? p.educationEntries : (Array.isArray(p.education) ? p.education : []));
        setExperienceEntries(Array.isArray(p.experienceEntries) ? p.experienceEntries : (Array.isArray(p.experience) ? p.experience : []));
        setProjectEntries(Array.isArray(p.projectEntries) ? p.projectEntries : []);
        setActivityEntries(Array.isArray(p.activityEntries) ? p.activityEntries : []);
        setCertificationEntries(Array.isArray(p.certificationEntries) ? p.certificationEntries : (Array.isArray(p.certifications) ? p.certifications : []));
        setReferenceEntries(Array.isArray(p.referenceEntries) ? p.referenceEntries : (Array.isArray(p.references) ? p.references : []));
        setTechnicalSkills(normalizeSkillStringArray(p.technicalSkills));
        setSoftSkills(normalizeSkillStringArray(p.softSkills));
        setLanguages(Array.isArray(p.languages) ? p.languages : []);
        setCareerInterests(Array.isArray(p.careerInterests) ? p.careerInterests : []);
        setProfessionalLinks(Array.isArray(p.professionalLinks) ? p.professionalLinks : (typeof p.professionalLinks === 'object' ? Object.entries(p.professionalLinks).map(([type, url]) => ({ type, url })) : []));
        
        // Set profile completion percentage from backend
        const completionPercentage = p.profileCompletion || p.profileCompletionPercentage || p.profileStatus?.completionPercentage || 0;
        setProfileCompletionPercentage(completionPercentage);
        
        setEditMode(false);
        setEditingSections({});
        setLoading(false);
      } catch (error) {
        console.error('Error loading other user profile:', error);
        setLoading(false);
      }
    };
    
    loadOtherUserProfile();
  }, [viewUserId, isViewingOtherProfile]);

  useEffect(() => {
    // Skip loading own profile if viewing another user
    if (isViewingOtherProfile) return;
    loadProfileData();
  }, []);

  // Debug: Log profileForm changes
  useEffect(() => {
    if (!loading && profileForm.firstName) {
      console.log('🔄 profileForm state changed:', {
        firstName: profileForm.firstName,
        lastName: profileForm.lastName,
        email: profileForm.email,
        currentCity: profileForm.currentCity,
        residentCountry: profileForm.residentCountry
      });
    }
  }, [profileForm.firstName, profileForm.lastName, profileForm.email, loading]);

  const loadProfileData = async () => {
    try {
      setLoading(true);
      const response = await axios.get(buildApiUrl('/api/intern/profile'), {
        headers: authHeaders()
      });
      
      const p = response.data || {};
      
      console.log('📥 InternMyProfile - Received profile data:', p);
      console.log('📊 Profile data summary:');
      console.log('  - firstName:', p.firstName, '(type:', typeof p.firstName, ')');
      console.log('  - lastName:', p.lastName, '(type:', typeof p.lastName, ')');
      console.log('  - email:', p.email, '(type:', typeof p.email, ')');
      console.log('  - phone:', p.phone, '(type:', typeof p.phone, ')');
      console.log('  - currentCity:', p.currentCity, '(type:', typeof p.currentCity, ')');
      console.log('  - residentCountry:', p.residentCountry, '(type:', typeof p.residentCountry, ')');
      console.log('  - nationality:', p.nationality, '(type:', typeof p.nationality, ')');
      console.log('  - educationEntries:', Array.isArray(p.educationEntries) ? p.educationEntries.length : 'not array', p.educationEntries);
      console.log('  - technicalSkills:', Array.isArray(p.technicalSkills) ? p.technicalSkills.length : 'not array', p.technicalSkills);
      console.log('  - profileCompletion:', p.profileCompletion);
      console.log('  - profilePhotoPath:', p.profilePhotoPath, '(type:', typeof p.profilePhotoPath, ')');
      
      // Check for 'None' strings and log them
      const noneFields = Object.keys(p).filter(key => p[key] === 'None' || (typeof p[key] === 'string' && p[key].trim() === 'None'));
      if (noneFields.length > 0) {
        console.warn('⚠️ Found fields with "None" string:', noneFields);
      }
      
      // Helper to convert 'None' strings to empty strings
      const cleanValue = (val) => {
        if (val === 'None' || (typeof val === 'string' && val.trim() === 'None')) {
          return '';
        }
        if (val === null || val === undefined) {
          return '';
        }
        return val || '';
      };
      
      // Helper to parse communitiesAssociations (could be string or array)
      const parseCommunitiesAssociations = (value) => {
        if (!value) return [];
        if (Array.isArray(value)) return value;
        if (typeof value === 'string') {
          try {
            const parsed = JSON.parse(value);
            return Array.isArray(parsed) ? parsed : [];
          } catch {
            return [];
          }
        }
        return [];
      };

      // Build the complete profile form object
      const updatedProfileForm = {
        firstName: cleanValue(p.firstName),
        lastName: cleanValue(p.lastName),
        middleName: cleanValue(p.middleName),
        email: cleanValue(p.email),
        phone: cleanValue(p.phone),
        altPhone: cleanValue(p.altPhone),
        ngo: cleanValue(p.ngo),
        hasDisability: p.hasDisability || 'no',
        disabilityDescription: cleanValue(p.disabilityDescription),
        communitiesAssociations: parseCommunitiesAssociations(p.communitiesAssociations),
        dateOfBirth: cleanValue(p.dateOfBirth),
        gender: cleanValue(p.gender),
        genderOther: cleanValue(p.genderOther),
        nationality: cleanValue(p.nationality),
        residentCountry: cleanValue(p.residentCountry),
        currentCity: cleanValue(p.currentCity),
        postalCode: cleanValue(p.postalCode),
        address: cleanValue(p.address),
        latitude: cleanValue(p.latitude),
        longitude: cleanValue(p.longitude),
        validDocs: cleanValue(p.validDocs),
        profilePhotoPath: cleanValue(p.profilePhotoPath),
        preferredLocation1: cleanValue(p.preferredLocation1),
        preferredLocation2: cleanValue(p.preferredLocation2),
        preferredLocation3: cleanValue(p.preferredLocation3),
        willingToRelocate: cleanValue(p.willingToRelocate),
        internshipMode: cleanValue(p.internshipMode),
        academicLevel: cleanValue(p.academicLevel),
        objective: cleanValue(p.objective),
        industryInterest: cleanValue(p.industryInterest),
        industryInterestOther: cleanValue(p.industryInterestOther),
        preferredRole: cleanValue(p.preferredRole),
        internshipDuration: cleanValue(p.internshipDuration),
        internshipDurationOther: cleanValue(p.internshipDurationOther),
        availability: cleanValue(p.availability),
        availabilityOther: cleanValue(p.availabilityOther),
        internshipStartMonthYear: cleanValue(p.internshipStartMonthYear || p.internshipTiming),
        expectedStipend: cleanValue(p.expectedStipend),
        currencyPreference: cleanValue(p.currencyPreference) || 'USD',
        currencyPreferenceOther: cleanValue(p.currencyPreferenceOther),
        unpaidWilling: cleanValue(p.unpaidWilling),
        academicCredit: cleanValue(p.academicCredit),
        hobbies: cleanValue(p.hobbies),
        whyInternship: cleanValue(p.whyInternship),
        additionalComments: cleanValue(p.additionalComments),
        participateInVolunteering: normalizeVolunteeringValue(p.participateInVolunteering),
        volunteeringDetails: cleanValue(p.volunteeringDetails),
        location: `${cleanValue(p.currentCity)}, ${cleanValue(p.residentCountry)}`.replace(', ,', '').trim()
      };
      
      console.log('📝 Setting profileForm with data:', {
        firstName: updatedProfileForm.firstName,
        lastName: updatedProfileForm.lastName,
        email: updatedProfileForm.email,
        currentCity: updatedProfileForm.currentCity,
        residentCountry: updatedProfileForm.residentCountry,
        nationality: updatedProfileForm.nationality
      });
      
      setProfileForm(updatedProfileForm);

      setEducationEntries(Array.isArray(p.educationEntries) ? p.educationEntries : []);
      setExperienceEntries(Array.isArray(p.experienceEntries) ? p.experienceEntries : []);
      setProjectEntries(Array.isArray(p.projectEntries) ? p.projectEntries : []);
      setActivityEntries(Array.isArray(p.activityEntries) ? p.activityEntries : []);
      setCertificationEntries(Array.isArray(p.certificationEntries) ? p.certificationEntries : []);
      setReferenceEntries(Array.isArray(p.referenceEntries) ? p.referenceEntries : []);
      setTechnicalSkills(normalizeSkillStringArray(p.technicalSkills));
      setSoftSkills(normalizeSkillStringArray(p.softSkills));
      setLanguages(Array.isArray(p.languages) ? p.languages : []);
      setCareerInterests(Array.isArray(p.careerInterests) ? p.careerInterests : []);
      setProfessionalLinks(Array.isArray(p.professionalLinks) ? p.professionalLinks : []);

      // Set profile completion percentage from backend
      const completionPercentage = p.profileCompletion || p.profileCompletionPercentage || p.profileStatus?.completionPercentage || 0;
      setProfileCompletionPercentage(completionPercentage);

      setLoading(false);
      
      // Log the data we just set (use the updatedProfileForm object, not the stale state)
      console.log('✅ Profile form state set with data:');
      console.log('  - firstName:', updatedProfileForm.firstName);
      console.log('  - lastName:', updatedProfileForm.lastName);
      console.log('  - email:', updatedProfileForm.email);
      console.log('  - phone:', updatedProfileForm.phone);
      console.log('  - currentCity:', updatedProfileForm.currentCity);
      console.log('  - residentCountry:', updatedProfileForm.residentCountry);
      console.log('  - nationality:', updatedProfileForm.nationality);
      console.log('  - academicLevel:', updatedProfileForm.academicLevel, '(from backend:', p.academicLevel, ')');
      console.log('  - participateInVolunteering:', updatedProfileForm.participateInVolunteering, '(from backend:', p.participateInVolunteering, ')');
      console.log('  - volunteeringDetails:', updatedProfileForm.volunteeringDetails ? 'has value' : 'empty', '(from backend:', p.volunteeringDetails ? 'has value' : 'empty', ')');
      console.log('  - educationEntries count:', Array.isArray(p.educationEntries) ? p.educationEntries.length : 0);
      console.log('  - technicalSkills count:', Array.isArray(p.technicalSkills) ? p.technicalSkills.length : 0);
      console.log('  - internshipMode:', p.internshipMode, '(type:', typeof p.internshipMode, ')');
    } catch (error) {
      console.error('Error loading profile:', error);
      setLoading(false);
    }
  };

  const handleResumeUploaded = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) throw new Error('No authentication token found');
      const populateResponse = await axios.post(
        buildApiUrl('/api/intern/populate-from-resume'),
        {},
        { headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' } }
      );
      if (populateResponse.data?.success) {
        setShowResumeUpload(false);
        await loadProfileData();
        setMessage('✅ Resume uploaded and profile updated successfully!');
        setTimeout(() => setMessage(''), 3000);
      } else {
        throw new Error(populateResponse.data?.error || 'Failed to populate profile');
      }
    } catch (error) {
      const errorMessage = error.response?.data?.error || error.message || 'Unknown error';
      setShowResumeUpload(false);
      alert(`⚠️ Resume uploaded, but profile update failed:\n\n${errorMessage}\n\nYou can manually edit your profile.`);
    }
  };

  const toggleSectionEdit = (section) => {
    if (isViewingOtherProfile) return;

    setEditingSections((prev) => {
      const updated = { ...prev };

      if (updated[section]) {
        delete updated[section];
      } else {
        updated[section] = true;
      }

      const hasActiveSections = Object.keys(updated).length > 0;
      setEditMode(hasActiveSections);

      return updated;
    });
  };

  const handlePhotoUpload = async (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 50 * 1024) {
        alert('Profile photo must be 50KB or smaller. Please compress your image.');
        e.target.value = '';
        return;
      }
      setProfilePhotoFile(file);
      const reader = new FileReader();
      reader.onload = (e) => setProfilePhotoPreview(e.target.result);
      reader.readAsDataURL(file);
      
      // Auto-save the photo immediately
      try {
        setSaving(true);
        setMessage('Uploading photo...');
        
        const formData = new FormData();
        formData.append('profilePhoto', file);
        
        // Include minimal profile data to ensure the save works
        const minimalPayload = {
          ...profileForm,
          educationEntries,
          experienceEntries,
          projectEntries,
          activityEntries,
          certificationEntries,
          referenceEntries,
          technicalSkills,
          softSkills,
          languages,
          careerInterests,
          professionalLinks,
          profileCompleted: profileForm.profileCompleted || 'true',
          isDraft: 'false'
        };
        
        // Append all form fields as JSON strings for complex data
        Object.keys(minimalPayload).forEach(key => {
          const value = minimalPayload[key];
          if (value !== null && value !== undefined) {
            if (Array.isArray(value) || (typeof value === 'object' && value !== null)) {
              formData.append(key, JSON.stringify(value));
            } else {
              formData.append(key, value);
            }
          }
        });
        
        const response = await axios.post(buildApiUrl('/api/intern/profile'), formData, {
          headers: {
            ...authHeaders(),
            'Content-Type': 'multipart/form-data'
          }
        });
        
        if (response.status === 200) {
          setMessage('✓ Photo uploaded successfully!');
          // Clear the photo file after successful upload
          setProfilePhotoFile(null);
          if (profilePhotoInputRef.current) {
            profilePhotoInputRef.current.value = '';
          }
          // Invalidate React Query cache instead of full reload
          queryClient.invalidateQueries({ queryKey: ['internProfile'] });
          queryClient.invalidateQueries({ queryKey: ['internProfile', false, null] });
          
          // Update profile photo path from response if available
          if (response.data?.profilePhotoPath) {
            // Update local state without full reload
            // The photo preview is already set, so we just need to update the path
            // This will be handled by the existing state
          }
          
          setTimeout(() => setMessage(''), 3000);
        }
      } catch (error) {
        console.error('Error uploading photo:', error);
        console.error('Error details:', error.response?.data);
        setMessage('❌ Error uploading photo. Please try again.');
        setTimeout(() => setMessage(''), 3000);
      } finally {
        setSaving(false);
      }
    }
  };

  const saveSection = async (section) => {
    try {
      setSaving(true);
      setMessage('');

      const payload = {
        ...profileForm,
        educationEntries,
        experienceEntries,
        projectEntries,
        activityEntries,
        certificationEntries,
        referenceEntries,
        technicalSkills,
        softSkills,
        languages,
        careerInterests,
        professionalLinks,
        // Mark as completed (not draft)
        profileCompleted: 'true',
        isDraft: 'false'
      };

      console.log('Saving intern profile section:', section);
      console.log('Payload:', payload);
      console.log('internshipMode in payload:', payload.internshipMode);

      let response;
      
      // If there's a profile photo file, use FormData
      if (profilePhotoFile) {
        const formData = new FormData();
        
        // Append all form fields as JSON strings for complex data
        Object.keys(payload).forEach(key => {
          const value = payload[key];
          if (value !== null && value !== undefined) {
            if (Array.isArray(value) || (typeof value === 'object' && value !== null)) {
              formData.append(key, JSON.stringify(value));
            } else {
              formData.append(key, value);
            }
          }
        });
        
        // Append the profile photo file
        formData.append('profilePhoto', profilePhotoFile);
        
        response = await axios.post(buildApiUrl('/api/intern/profile'), formData, {
          headers: {
            ...authHeaders(),
            'Content-Type': 'multipart/form-data'
          }
        });
        
        // Clear the photo file after successful upload
        setProfilePhotoFile(null);
        setProfilePhotoPreview(null);
        if (profilePhotoInputRef.current) {
          profilePhotoInputRef.current.value = '';
        }
      } else {
        // Use JSON for regular updates
        response = await axios.post(buildApiUrl('/api/intern/profile'), payload, {
          headers: authHeaders()
        });
      }

      if (response.status === 200) {
        setMessage('✓ Profile updated successfully!');

        if (section === 'all') {
          setEditingSections({});
          setEditMode(false);
        } else {
          setEditingSections((prev) => {
            const updated = { ...prev };
            delete updated[section];

            if (Object.keys(updated).length === 0) {
              setEditMode(false);
            }

            return updated;
          });
        }

        // Invalidate React Query cache instead of full reload
        // This updates the dashboard cache in the background without page refresh
        queryClient.invalidateQueries({ queryKey: ['internProfile'] });
        queryClient.invalidateQueries({ queryKey: ['internProfile', false, null] });
        
        // Update profile completion from response if available
        if (response.data?.profileCompletion !== undefined) {
          setProfileCompletionPercentage(response.data.profileCompletion);
        } else {
          // Recalculate completion percentage locally without full reload
          const updatedProfile = { ...profileForm, ...payload };
          const requiredFields = [
            'firstName', 'lastName', 'email', 'phone', 'dateOfBirth', 'gender',
            'nationality', 'currentCity', 'academicLevel', 'objective', 'preferredRole'
          ];
          const arrayFields = [
            'educationEntries', 'technicalSkills', 'softSkills', 'languages'
          ];
          const filledSimpleFields = requiredFields.filter(field => {
            const value = updatedProfile[field];
            return value && value.toString().trim() !== '';
          }).length;
          const filledArrayFields = arrayFields.filter(field => {
            const value = updatedProfile[field];
            return Array.isArray(value) && value.length > 0;
          }).length;
          const simplePercentage = (filledSimpleFields / requiredFields.length) * 60;
          const arrayPercentage = (filledArrayFields / arrayFields.length) * 40;
          setProfileCompletionPercentage(Math.round(simplePercentage + arrayPercentage));
        }
        
        setTimeout(() => setMessage(''), 3000);
      }
    } catch (error) {
      console.error('Error saving section:', error);
      console.error('Error details:', error.response?.data);
      setMessage('❌ Error saving. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const saveProfile = async () => {
    await saveSection('all');
  };

  if (loading) {
    return (
      <ThemedLoadingSpinner 
        theme="intern"
        text="Loading your intern profile..."
        subText="Please wait while we fetch your data"
        fullScreen={true}
      />
    );
  }

  const sidebarWidth = 280;
  const sidebarStyles = {
    width: isMobileView ? 'min(88vw, 320px)' : `${sidebarWidth}px`,
    background: 'linear-gradient(180deg, #f97316 0%, #0d9488 100%)',
    padding: isMobileView ? '1.25rem 1rem' : '2rem 1.5rem',
    boxShadow: isMobileView ? '6px 0 24px rgba(15, 23, 42, 0.25)' : '4px 0 20px rgba(249, 115, 22, 0.15)',
    position: 'fixed',
    left: 0,
    top: isMobileView ? MOBILE_HEADER_HEIGHT : 0,
    height: isMobileView ? `calc(100vh - ${MOBILE_HEADER_HEIGHT}px)` : '100vh',
    overflowY: 'auto',
    zIndex: isMobileView ? 1150 : 1000,
    color: '#ffffff',
    transform: isMobileView ? (mobileSidebarOpen ? 'translateX(0)' : 'translateX(-100%)') : 'translateX(0)',
    transition: 'transform 0.3s ease',
    pointerEvents: isMobileView && !mobileSidebarOpen ? 'none' : 'auto'
  };

  const mainContentStyles = {
    marginLeft: isMobileView ? 0 : `${sidebarWidth}px`,
    width: isMobileView ? '100%' : `calc(100% - ${sidebarWidth}px)`,
    maxWidth: isMobileView ? '100%' : `calc(100% - ${sidebarWidth}px)`,
    overflowX: 'hidden',
    overflowY: 'auto',
    minHeight: isMobileView ? 'auto' : '100vh',
    boxSizing: 'border-box',
    backgroundColor: '#f8fafc',
    paddingTop: 0,
    flex: '1 1 auto',
    minWidth: 0
  };

  const responsiveContainerStyle = {
    ...containerStyle,
    padding: isMobileView ? '12px 12px 72px 12px' : containerStyle.padding,
    maxWidth: '100%',
    width: '100%',
    overflowX: 'hidden'
  };

  const sectionCardHeaderStyle = {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '20px',
    paddingBottom: '15px',
    borderBottom: '2px solid #f0f0f0'
  };

  return (
    <div className="my-profile-page intern-my-profile-page" style={{ 
      display: 'flex', 
      flexDirection: isMobileView ? 'column' : 'row',
      minHeight: '100vh', 
      width: '100%',
      backgroundColor: '#f8fafc',
      overflowX: 'hidden'
    }}>
      {showScrollTop && isMobileView && (
        <button
          type="button"
          className="scroll-top-btn"
          onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
          style={{
            position: 'fixed',
            right: '16px',
            bottom: '24px',
            width: '48px',
            height: '48px',
            borderRadius: '50%',
            border: 'none',
            background: 'linear-gradient(135deg, #f97316 0%, #0d9488 100%)',
            color: '#ffffff',
            boxShadow: '0 10px 24px rgba(15, 23, 42, 0.3)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 999,
            cursor: 'pointer'
          }}
          aria-label="Scroll to top"
        >
          <FontAwesomeIcon icon={faArrowUp} />
        </button>
      )}
      {isMobileView && (
        <header className="intern-profile-mobile-header">
          <div className="intern-profile-header-start">
            <button
              type="button"
              className="intern-profile-menu-btn btn-icon"
              onClick={() => setMobileSidebarOpen(prev => !prev)}
              aria-label={mobileSidebarOpen ? 'Close profile navigation' : 'Open profile navigation'}
            >
              <FontAwesomeIcon icon={mobileSidebarOpen ? faTimes : faBars} />
            </button>
          </div>
          <h1 className="intern-profile-mobile-title">
            {isViewingOtherProfile ? 'Intern Profile' : 'My Profile'}
          </h1>
          <div className="intern-profile-header-end">
            <button
              type="button"
              className="intern-profile-back-btn"
              onClick={() => (isViewingOtherProfile ? navigate(-1) : navigate('/intern-dashboard'))}
            >
              {isViewingOtherProfile ? 'Back' : 'Dashboard'}
            </button>
          </div>
        </header>
      )}
      {isMobileView && mobileSidebarOpen && (
        <div
          className="intern-profile-sidebar-overlay"
          style={{ top: MOBILE_HEADER_HEIGHT }}
          onClick={() => setMobileSidebarOpen(false)}
          aria-hidden="true"
        />
      )}
      {/* Sidebar */}
      <div style={sidebarStyles} className="sidebar-container intern-profile-sidebar">
        <div style={{ marginBottom: '2rem' }}>
          <h2 style={{ 
            color: '#ffffff', 
            fontSize: '1.5rem', 
            fontWeight: '800', 
            marginBottom: '0.5rem',
            textShadow: '0 4px 8px rgba(0,0,0,0.7)',
            letterSpacing: '0.5px'
          }}>
            My Profile
          </h2>
          <p style={{ 
            color: '#ffffff', 
            fontSize: '0.9rem',
            margin: 0,
            fontWeight: '700',
            textShadow: '0 2px 4px rgba(0,0,0,0.6)'
          }}>
            Manage your internship profile
          </p>
        </div>
        
        {/* Quick Navigation */}
        <div style={{ marginBottom: '2rem' }}>
          <h3 style={{ 
            color: '#ffffff', 
            fontSize: '1.1rem', 
            fontWeight: '700', 
            marginBottom: '1rem',
            textShadow: '0 3px 6px rgba(0,0,0,0.7)',
            letterSpacing: '0.3px'
          }}>
            Quick Actions
          </h3>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
            {[
              { name: 'Personal Info', icon: '👤', dataSection: 'personal' },
              { name: 'Nationality', icon: '🌍', dataSection: 'nationality' },
              { name: 'Locations', icon: '📍', dataSection: 'locations' },
              { name: 'Education', icon: '🎓', dataSection: 'education' },
              { name: 'Objective', icon: '🎯', dataSection: 'objective' },
              { name: 'Experience', icon: '💼', dataSection: 'experience' },
              { name: 'Skills', icon: '⚡', dataSection: 'skills' },
              { name: 'Languages', icon: '🗣️', dataSection: 'languages' },
              { name: 'Projects', icon: '🚀', dataSection: 'projects' },
              { name: 'Activities', icon: '🏆', dataSection: 'activities' },
              { name: 'Certifications', icon: '📜', dataSection: 'certifications' },
              { name: 'References', icon: '👥', dataSection: 'references' },
              { name: 'Online Links', icon: '🔗', dataSection: 'links' },
              { name: 'Preferences', icon: '⚙️', dataSection: 'preferences' },
              { name: 'Additional', icon: '📝', dataSection: 'additional' }
            ].map((item, idx) => (
              <button 
                key={idx}
                onClick={() => {
                  const element = document.querySelector(`[data-section="${item.dataSection}"]`);
                  if (element) element.scrollIntoView({ behavior: 'smooth' });
                  if (isMobileView) {
                    setMobileSidebarOpen(false);
                  }
                }}
                style={{
                  background: 'rgba(0,0,0,0.3)',
                  border: '2px solid rgba(255,255,255,0.4)',
                  color: '#ffffff',
                  padding: '0.75rem 1rem',
                  borderRadius: '8px',
                  fontSize: '0.9rem',
                  fontWeight: '700',
                  cursor: 'pointer',
                  transition: 'all 0.3s ease',
                  textAlign: 'left',
                  boxShadow: '0 2px 8px rgba(249, 115, 22, 0.3)'
                }}
                onMouseEnter={(e) => {
                  e.target.style.background = 'rgba(0,0,0,0.5)';
                  e.target.style.transform = 'translateX(4px)';
                  e.target.style.borderColor = 'rgba(255,255,255,0.6)';
                }}
                onMouseLeave={(e) => {
                  e.target.style.background = 'rgba(0,0,0,0.3)';
                  e.target.style.transform = 'translateX(0)';
                  e.target.style.borderColor = 'rgba(255,255,255,0.4)';
                }}
              >
                {item.icon} {item.name}
              </button>
            ))}
            {canEditProfile && (
              <>
              <button
                onClick={() => setShowResumeUpload(true)}
                style={{
                  background: 'linear-gradient(135deg, rgba(249, 115, 22, 0.5) 0%, rgba(34, 197, 94, 0.4) 100%)',
                  border: '2px solid rgba(255,255,255,0.5)',
                  color: '#ffffff',
                  padding: '0.75rem 1rem',
                  borderRadius: '8px',
                  fontSize: '0.9rem',
                  fontWeight: '700',
                  cursor: 'pointer',
                  transition: 'all 0.3s ease',
                  textAlign: 'left',
                  boxShadow: '0 2px 8px rgba(249, 115, 22, 0.4)'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'translateX(4px)';
                  e.currentTarget.style.borderColor = 'rgba(255,255,255,0.8)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateX(0)';
                  e.currentTarget.style.borderColor = 'rgba(255,255,255,0.5)';
                }}
              >
                📄 Upload / Update Resume
              </button>
              <button
                type="button"
                style={{
                  width: '100%',
                  marginTop: '0.6rem',
                  background: 'linear-gradient(135deg, #f97316, #22c55e)',
                  border: '2px solid rgba(255,255,255,0.6)',
                  color: '#ffffff',
                  padding: '0.75rem 1rem',
                  borderRadius: '8px',
                  fontSize: '0.9rem',
                  fontWeight: '700',
                  cursor: 'pointer',
                  transition: 'all 0.3s ease',
                  textAlign: 'left',
                  boxShadow: '0 2px 8px rgba(34, 197, 94, 0.4)'
                }}
                onClick={async () => {
                  try {
                    const res = await fetch(buildApiUrl('/api/intern/resume'), {
                      method: 'GET',
                      credentials: 'include',
                      headers: {
                        Accept: 'application/pdf',
                        Authorization: `Bearer ${localStorage.getItem('token') || ''}`,
                      },
                    });

                    if (!res.ok) {
                      console.error('Download intern resume failed', await res.text());
                      alert('Could not download resume. Please try again.');
                      return;
                    }

                    const blob = await res.blob();
                    const url = window.URL.createObjectURL(blob);
                    const a = document.createElement('a');
                    a.href = url;
                    a.download = resolveDownloadFilename(res, profileForm, 'Resume.pdf');
                    document.body.appendChild(a);
                    a.click();
                    window.URL.revokeObjectURL(url);
                    document.body.removeChild(a);
                  } catch (e) {
                    console.error('Download intern resume error', e);
                    alert('Could not download resume. Please try again.');
                  }
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'translateX(4px)';
                  e.currentTarget.style.borderColor = 'rgba(255,255,255,0.9)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateX(0)';
                  e.currentTarget.style.borderColor = 'rgba(255,255,255,0.6)';
                }}
              >
                🚀 Download Smart Resume
              </button>
              </>
            )}
          </div>
        </div>


        {/* Profile Completion */}
        <div style={{ marginTop: '2rem' }}>
          <h3 style={{ 
            color: '#ffffff', 
            fontSize: '1rem', 
            fontWeight: '700', 
            marginBottom: '1rem',
            textShadow: '0 3px 6px rgba(0,0,0,0.7)'
          }}>
            Profile Completion
          </h3>
          <div style={{ 
            background: 'rgba(0,0,0,0.3)', 
            padding: '1rem', 
            borderRadius: '8px',
            border: '2px solid rgba(255,255,255,0.3)'
          }}>
            <div style={{ 
              display: 'flex', 
              justifyContent: 'space-between', 
              marginBottom: '10px',
              color: '#ffffff',
              fontSize: '14px',
              fontWeight: '700'
            }}>
              <span>{profileCompletionPercentage}%</span>
              <span>Complete</span>
            </div>
            <div style={{ 
              background: 'rgba(255,255,255,0.2)', 
              height: '8px', 
              borderRadius: '4px', 
              overflow: 'hidden'
            }}>
              <div style={{
                background: '#ffffff',
                height: '100%',
                width: `${profileCompletionPercentage}%`,
                borderRadius: '4px',
                boxShadow: '0 1px 3px rgba(0,0,0,0.3)',
                transition: 'width 0.5s ease-in-out'
              }}></div>
            </div>
          </div>
        </div>

        {/* Tips */}
        {!(isViewingOtherProfile && (isEmployerOrRecruiterViewer || isAdminViewer)) && (
          <div style={{ marginTop: '2rem' }}>
            <h3 style={{ 
              color: '#ffffff', 
              fontSize: '1.1rem', 
              fontWeight: '700', 
              marginBottom: '1rem',
              textShadow: '0 3px 6px rgba(0,0,0,0.7)',
              letterSpacing: '0.3px'
            }}>
              Tips
            </h3>
            <div style={{ 
              background: 'rgba(0,0,0,0.4)', 
              padding: '1rem', 
              borderRadius: '8px',
              border: '2px solid rgba(255,255,255,0.3)',
              boxShadow: '0 4px 12px rgba(249, 115, 22, 0.2)'
            }}>
              <p style={{ 
                color: '#ffffff', 
                fontSize: '0.85rem', 
                margin: 0,
                lineHeight: '1.4',
                fontWeight: '700',
                textShadow: '0 2px 4px rgba(0,0,0,0.7)'
              }}>
                💡 Keep your profile updated to get better internship matches
              </p>
            </div>
          </div>
        )}
      </div>

      {/* Main Content */}
      <div className="main-content" style={mainContentStyles}>
        <div style={responsiveContainerStyle}>
          {/* Profile Header */}
          <div style={profileHeaderStyle} className="profile-header">
            <div style={headerPatternStyle}></div>
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                position: 'relative',
                zIndex: 2,
                width: '100%',
                gap: '20px',
                flexWrap: isMobileView ? 'wrap' : 'nowrap'
              }}
            >
              {/* Left Section: Avatar and Name */}
              <div className="profile-header-identity" style={{ display: 'flex', alignItems: 'center', gap: isMobileView ? '12px' : '20px', flex: isMobileView ? '1 1 100%' : '0 0 auto', minWidth: 0 }}>
              <div style={{ position: 'relative' }}>
                <div style={profileAvatarStyle}>
                  {(profilePhotoPreview || profileForm.profilePhotoPath) ? (() => {
                    // Build image URL from profilePhotoPath or preview
                    let imageUrl = '';
                    if (profilePhotoPreview) {
                      imageUrl = profilePhotoPreview;
                    } else {
                      const normalizedPath = profileForm.profilePhotoPath.replace(/\\/g, '/');
                      if (normalizedPath.startsWith('uploads/intern_photos/')) {
                        const uploadPath = normalizedPath.replace('uploads/intern_photos/', 'intern_photos/');
                        imageUrl = buildApiUrl(`/api/users/uploads/${uploadPath}`);
                      } else if (normalizedPath.startsWith('uploads/')) {
                        const uploadPath = normalizedPath.replace('uploads/', '');
                        imageUrl = buildApiUrl(`/api/users/uploads/${uploadPath}`);
                      } else if (normalizedPath.startsWith('/uploads/')) {
                        const uploadPath = normalizedPath.replace('/uploads/', '');
                        imageUrl = buildApiUrl(`/api/users/uploads/${uploadPath}`);
                      } else {
                        // Assume it's already a relative path
                        imageUrl = buildApiUrl(`/api/users/uploads/${normalizedPath}`);
                      }
                    }
                    return (
                      <img 
                        src={imageUrl}
                        alt={`${profileForm.firstName} ${profileForm.lastName}`}
                        style={{
                          position: 'absolute',
                          top: 0,
                          left: 0,
                          width: '100%',
                          height: '100%',
                          objectFit: 'cover',
                          borderRadius: '50%',
                          border: '3px solid rgba(255, 255, 255, 0.4)'
                        }}
                        onError={(e) => {
                          // Fallback to initials if image fails to load
                          e.target.style.display = 'none';
                        }}
                      />
                    );
                  })() : null}
                  <div style={{
                    display: (profilePhotoPreview || profileForm.profilePhotoPath) ? 'none' : 'flex',
                    width: '100%',
                    height: '100%',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}>{(profileForm.firstName?.[0] || 'I')}{(profileForm.lastName?.[0] || 'S')}</div>
                </div>
                {/* Upload button - positioned outside avatar, matching jobseeker style */}
                {canEditProfile && (
                  <label
                    style={{
                      position: 'absolute',
                      bottom: '0',
                      right: '0',
                      background: '#f97316',
                      color: 'white',
                      border: '3px solid white',
                      borderRadius: '50%',
                      width: '32px',
                      height: '32px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      cursor: 'pointer',
                      boxShadow: '0 2px 8px rgba(0,0,0,0.3)',
                      fontSize: '14px',
                      transition: 'all 0.2s ease',
                      zIndex: 10
                    }}
                    title="Change Photo"
                    onMouseEnter={(e) => {
                      e.currentTarget.style.background = '#ea580c';
                      e.currentTarget.style.transform = 'scale(1.1)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.background = '#f97316';
                      e.currentTarget.style.transform = 'scale(1)';
                    }}
                  >
                    <FontAwesomeIcon icon={faCamera} />
                    <input
                      type="file"
                      ref={profilePhotoInputRef}
                      accept="image/*"
                      onChange={handlePhotoUpload}
                      style={{ display: 'none' }}
                    />
                  </label>
                )}
              </div>
                <div className="profile-header-details" style={{ minWidth: 0, flex: 1 }}>
                <h2
                  style={{
                    margin: 0,
                    fontSize: isMobileView ? '1.25rem' : '26px',
                    fontWeight: '700',
                    color: '#ffffff'
                  }}
                >
                  {profileForm.firstName || 'N/A'} {profileForm.lastName || ''}
                </h2>
                <p
                  className="profile-location-row"
                  style={{
                    margin: 0,
                    fontSize: '14px',
                    fontWeight: '500',
                    color: 'rgba(255,255,255,0.9)',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px',
                    flexWrap: isMobileView ? 'wrap' : 'nowrap'
                  }}
                >
                  <FontAwesomeIcon icon={faMapMarkerAlt} />
                  <span className="profile-location-text">{getPrimaryLocationDisplay()}</span>
                  {(profileForm.latitude || profileForm.longitude || getPrimaryLocationDisplay() !== '—') && (
                    <button
                      type="button"
                      className="intern-profile-maps-btn btn-icon btn-sm"
                      onClick={openLocationInMaps}
                      aria-label="Open location in Google Maps"
                    >
                      <FontAwesomeIcon icon={faExternalLinkAlt} />
                    </button>
                  )}
                </p>
              </div>
              </div>
              
              {/* Right Section: Back to Dashboard Button */}
                {(!isMobileView || mobileSidebarOpen) && (
                <div
                  style={{
                    display: 'flex',
                    gap: '10px',
                    flexWrap: 'wrap',
                    justifyContent: 'flex-end',
                    marginLeft: 'auto',
                    flex: '0 0 auto'
                  }}
                >
                    <button
                    onClick={() => navigate('/intern-dashboard')}
                      style={{
                      background: 'linear-gradient(135deg, #f97316 0%, #fb923c 100%)',
                      color: '#ffffff',
                      border: 'none',
                        padding: '10px 20px',
                        borderRadius: '10px',
                        fontWeight: '600',
                        fontSize: '14px',
                        cursor: 'pointer',
                      boxShadow: '0 6px 15px rgba(249, 115, 22, 0.4)',
                      display: 'inline-flex',
                        alignItems: 'center',
                        gap: '8px'
                      }}
                    >
                    ← Back to Dashboard
                    </button>
                  </div>
                )}
            </div>
            {!isViewingOtherProfile && isMobileView && (
              <div className="intern-profile-complete-wrap" style={{ marginTop: '12px', width: '100%' }}>
                <button
                  type="button"
                  className="intern-profile-complete-btn btn-block"
                  onClick={() => navigate('/intern-registration')}
                >
                  <FontAwesomeIcon icon={faUserCheck} /> Complete Profile
                </button>
              </div>
            )}
          </div>

          {/* Success/Error Message */}
          {message && (
            <div style={{
              padding: '15px',
              marginBottom: '20px',
              borderRadius: '12px',
              background: message.includes('✓') ? '#d4edda' : '#f8d7da',
              color: message.includes('✓') ? '#155724' : '#721c24',
              border: `1px solid ${message.includes('✓') ? '#c3e6cb' : '#f5c6cb'}`,
              fontSize: '14px',
              fontWeight: '600'
            }}>
              {message}
            </div>
          )}

          {/* Section 1: Personal Information */}
          <div className="card" style={elevatedCardStyle} data-section="personal">
            <div className="card-header" style={sectionCardHeaderStyle}>
              <h3 className="card-title"><FontAwesomeIcon icon={faUser} /> Personal Information</h3>
              <div className="card-header-actions" style={{ display: 'flex', gap: '8px' }}>
                {!editingSections.personal ? (
                  <button 
                    onClick={() => toggleSectionEdit('personal')}
                    style={{ 
                      background: 'linear-gradient(135deg, #f97316 0%, #0d9488 100%)', 
                      color: 'white', 
                      border: 'none',
                      padding: '8px 16px',
                      borderRadius: '6px',
                      fontSize: '14px',
                      fontWeight: '600',
                      cursor: 'pointer'
                    }}
                  >
                    <FontAwesomeIcon icon={faEdit} /> Edit
                  </button>
                ) : (
                  <>
                    <button 
                      onClick={() => saveSection('personal')} 
                      disabled={saving}
                      style={{ 
                        background: '#10b981', 
                        color: 'white', 
                        border: 'none',
                        padding: '8px 16px',
                        borderRadius: '6px',
                        fontSize: '14px',
                        fontWeight: '600',
                        cursor: saving ? 'not-allowed' : 'pointer'
                      }}
                    >
                      <FontAwesomeIcon icon={faSave} /> {saving ? 'Saving...' : 'Save'}
                    </button>
                    <button 
                      onClick={() => toggleSectionEdit('personal')}
                      style={{
                        background: '#dc2626',
                        color: 'white',
                        border: 'none',
                        padding: '8px 16px',
                        borderRadius: '6px',
                        fontSize: '14px',
                        fontWeight: '600',
                        cursor: 'pointer'
                      }}
                    >
                      <FontAwesomeIcon icon={faTimes} /> Cancel
                    </button>
                  </>
                )}
              </div>
            </div>

            {/* Personal Info Fields */}
            <div className="profile-form-grid" style={formGridStyle('repeat(3, 1fr)', { marginBottom: '15px' })}>
              <div className="form-group">
                <label className="form-label">First Name <span style={{ color: '#dc2626' }}>*</span></label>
                <input 
                  type="text"
                  style={{ ...formInputBase, ...viewModeField }} 
                  value={profileForm.firstName || currentUser?.firstName || ''} 
                  disabled={!editingSections.personal} 
                  onChange={(e) => setProfileForm(p => ({ ...p, firstName: e.target.value }))} 
                  placeholder="Enter your first name"
                />
              </div>
              <div className="form-group">
                <label className="form-label">Middle Name</label>
                <input 
                  type="text"
                  style={{ ...formInputBase, ...viewModeField }} 
                  value={profileForm.middleName || ''} 
                  disabled={!editingSections.personal} 
                  onChange={(e) => setProfileForm(p => ({ ...p, middleName: e.target.value }))} 
                  placeholder="Enter your middle name"
                />
              </div>
              <div className="form-group">
                <label className="form-label">Last Name <span style={{ color: '#dc2626' }}>*</span></label>
                <input 
                  type="text"
                  style={{ ...formInputBase, ...viewModeField }} 
                  value={profileForm.lastName || currentUser?.lastName || ''} 
                  disabled={!editingSections.personal} 
                  onChange={(e) => setProfileForm(p => ({ ...p, lastName: e.target.value }))} 
                  placeholder="Enter your last name"
                />
              </div>
            </div>

            <div className="profile-form-grid" style={formGridStyle('repeat(3, 1fr)', { marginBottom: '15px' })}>
              <div className="form-group">
                <label className="form-label">Date of Birth <span style={{ color: '#dc2626' }}>*</span></label>
                <input 
                  type="date"
                  style={{ ...formInputBase, ...viewModeField }} 
                  value={profileForm.dateOfBirth || ''} 
                  disabled={!editingSections.personal} 
                  onChange={(e) => setProfileForm(p => ({ ...p, dateOfBirth: e.target.value }))}
                />
              </div>
              <div className="form-group">
                <label className="form-label">Gender <span style={{ color: '#dc2626' }}>*</span></label>
                <select 
                  style={{ ...formInputBase, ...viewModeField }} 
                  value={profileForm.gender || ''} 
                  disabled={!editingSections.personal} 
                  onChange={(e) => setProfileForm(p => ({ ...p, gender: e.target.value }))}
                >
                  <option value="">Select gender</option>
                  <option value="male">Male</option>
                  <option value="female">Female</option>
                  <option value="other">Other</option>
                  <option value="prefer-not-to-say">Prefer not to say</option>
                </select>
              </div>
            </div>

            <div className="profile-form-grid" style={formGridStyle('repeat(3, 1fr)', { marginBottom: '15px' })}>
              <div className="form-group">
                <label className="form-label">Email <span style={{ color: '#dc2626' }}>*</span></label>
                <input 
                  type="email"
                  style={{ ...formInputBase, background: '#f7f9fc', borderColor: '#e6eaf1' }} 
                  value={profileForm.email || currentUser?.email || ''} 
                  disabled
                />
              </div>
              <div className="form-group">
                <label className="form-label">Phone <span style={{ color: '#dc2626' }}>*</span></label>
                <input 
                  type="tel"
                  style={{ ...formInputBase, ...viewModeField }} 
                  value={profileForm.phone || currentUser?.phone || ''} 
                  disabled={!editingSections.personal} 
                  onChange={(e) => setProfileForm(p => ({ ...p, phone: e.target.value }))}
                  placeholder="+254 700 000 000"
                />
              </div>
              <div className="form-group">
                <label className="form-label">Alternative Phone</label>
                <input 
                  type="tel"
                  style={{ ...formInputBase, ...viewModeField }} 
                  value={profileForm.altPhone || ''} 
                  disabled={!editingSections.personal} 
                  onChange={(e) => setProfileForm(p => ({ ...p, altPhone: e.target.value }))}
                  placeholder="Parent/Guardian number"
                />
              </div>
            </div>
          </div>

          <div style={{ marginTop: '15px' }}>
            <div className="form-group" style={{ width: '100%' }}>
              <label className="form-label">Community / Association</label>
              {editingSections.personal ? (
                <CommunityAssociationSelector
                  value={profileForm.communitiesAssociations || []}
                  onChange={(items) => setProfileForm(p => ({ ...p, communitiesAssociations: items }))}
                />
              ) : (
                <div style={{ padding: '12px', background: '#f9f9f9', borderRadius: '6px', minHeight: '40px' }}>
                  {profileForm.communitiesAssociations && profileForm.communitiesAssociations.length > 0 ? (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                      {profileForm.communitiesAssociations.map((item, index) => (
                        <div key={index} style={{
                          padding: '8px 12px',
                          background: 'white',
                          borderRadius: '4px',
                          border: '1px solid #ddd',
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center'
                        }}>
                          <div>
                            <strong>{item.name}</strong>
                            <span style={{
                              marginLeft: '10px',
                              padding: '2px 8px',
                              background: item.type === 'association' ? '#e3f2fd' : '#f3e5f5',
                              color: item.type === 'association' ? '#1976d2' : '#7b1fa2',
                              borderRadius: '12px',
                              fontSize: '11px',
                              fontWeight: '500'
                            }}>
                              {item.type === 'association' ? 'Association' : 'Community'}
                            </span>
                            {item.country && (
                              <span style={{ marginLeft: '8px', fontSize: '12px', color: '#666' }}>
                                • {item.country}
                              </span>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <span style={{ color: '#999', fontStyle: 'italic' }}>No communities/associations added</span>
                  )}
                </div>
              )}
            </div>
          </div>

          <div style={{ marginTop: '15px' }}>
            <div className="form-group" style={{ width: '100%', overflow: 'visible' }}>
              <label className="form-label">NGO</label>
              {editingSections.personal ? (
                <NGOSelector
                  value={profileForm.ngo || ''}
                  onChange={(ngo) => setProfileForm(p => ({ ...p, ngo }))}
                />
              ) : (
                <div style={{ padding: '12px', background: '#f9f9f9', borderRadius: '6px', minHeight: '40px' }}>
                  {profileForm.ngo ? (
                    <span style={{ fontWeight: '500' }}>{profileForm.ngo}</span>
                  ) : (
                    <span style={{ color: '#999', fontStyle: 'italic' }}>No NGO selected</span>
                  )}
                </div>
              )}
            </div>
          </div>

          <div style={{ marginTop: '15px' }}>
            <div className="form-group" style={{ width: '100%' }}>
              <label className="form-label">Do you have any disability?</label>
              {editingSections.personal ? (
                <>
                  <div style={{ display: 'flex', gap: '20px', marginTop: '8px', marginBottom: '10px' }}>
                    <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer', fontWeight: 'normal' }}>
                      <input
                        type="radio"
                        name="hasDisability"
                        value="no"
                        checked={profileForm.hasDisability === 'no'}
                        onChange={(e) => setProfileForm(p => ({ ...p, hasDisability: e.target.value, disabilityDescription: e.target.value === 'no' ? '' : p.disabilityDescription }))}
                        style={{ marginRight: '8px' }}
                      />
                      No
                    </label>
                    <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer', fontWeight: 'normal' }}>
                      <input
                        type="radio"
                        name="hasDisability"
                        value="yes"
                        checked={profileForm.hasDisability === 'yes'}
                        onChange={(e) => setProfileForm(p => ({ ...p, hasDisability: e.target.value }))}
                        style={{ marginRight: '8px' }}
                      />
                      Yes
                    </label>
                  </div>
                  {profileForm.hasDisability === 'yes' && (
                    <textarea
                      name="disabilityDescription"
                      placeholder="Please describe"
                      value={profileForm.disabilityDescription || ''}
                      onChange={(e) => setProfileForm(p => ({ ...p, disabilityDescription: e.target.value }))}
                      rows={4}
                      style={{
                        width: '100%',
                        padding: '10px',
                        border: '1px solid #ddd',
                        borderRadius: '4px',
                        fontSize: '14px',
                        fontFamily: 'inherit',
                        marginTop: '8px',
                        resize: 'vertical'
                      }}
                    />
                  )}
                </>
              ) : (
                <div style={{ padding: '12px', background: '#f9f9f9', borderRadius: '6px', minHeight: '40px' }}>
                  {profileForm.hasDisability === 'yes' ? (
                    <div>
                      <div style={{ fontWeight: '500', marginBottom: '8px' }}>Yes</div>
                      {profileForm.disabilityDescription && (
                        <div style={{ color: '#666', fontSize: '14px', whiteSpace: 'pre-wrap' }}>
                          {profileForm.disabilityDescription}
                        </div>
                      )}
                    </div>
                  ) : (
                    <span style={{ color: '#999', fontStyle: 'italic' }}>No</span>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Section 2: Nationality & Residency */}
          <div className="card" style={elevatedCardStyle} data-section="nationality">
            <div className="card-header" style={sectionCardHeaderStyle}>
              <h3 className="card-title"><FontAwesomeIcon icon={faPassport} /> Nationality & Residency</h3>
              <div className="card-header-actions" style={{ display: 'flex', gap: '8px' }}>
                {!editingSections.nationality ? (
                  <button 
                    onClick={() => toggleSectionEdit('nationality')}
                    style={{ 
                      background: 'linear-gradient(135deg, #f97316 0%, #0d9488 100%)', 
                      color: 'white', 
                      border: 'none',
                      padding: '8px 16px',
                      borderRadius: '6px',
                      fontSize: '14px',
                      fontWeight: '600',
                      cursor: 'pointer'
                    }}
                  >
                    <FontAwesomeIcon icon={faEdit} /> Edit
                  </button>
                ) : (
                  <>
                    <button 
                      onClick={() => saveSection('nationality')} 
                      disabled={saving}
                      style={{ 
                        background: '#10b981', 
                        color: 'white', 
                        border: 'none',
                        padding: '8px 16px',
                        borderRadius: '6px',
                        fontSize: '14px',
                        fontWeight: '600'
                      }}
                    >
                      <FontAwesomeIcon icon={faSave} /> {saving ? 'Saving...' : 'Save'}
                    </button>
                    <button 
                      onClick={() => toggleSectionEdit('nationality')}
                      style={{
                        background: '#dc2626',
                        color: 'white',
                        border: 'none',
                        padding: '8px 16px',
                        borderRadius: '6px',
                        fontSize: '14px',
                        fontWeight: '600'
                      }}
                    >
                      <FontAwesomeIcon icon={faTimes} /> Cancel
                    </button>
                  </>
                )}
              </div>
            </div>

            <div className="profile-form-grid" style={formGridStyle('repeat(2, 1fr)')}>
              <div className="form-group">
                <label className="form-label">Nationality</label>
                <input 
                  type="text"
                  style={{ ...formInputBase, ...viewModeField }} 
                  value={profileForm.nationality || ''} 
                  disabled={!editingSections.nationality} 
                  onChange={(e) => setProfileForm(p => ({ ...p, nationality: e.target.value }))}
                  placeholder="e.g., Kenyan"
                />
              </div>
              <div className="form-group">
                <label className="form-label">Resident Country</label>
                <input 
                  type="text"
                  style={{ ...formInputBase, ...viewModeField }} 
                  value={profileForm.residentCountry || ''} 
                  disabled={!editingSections.nationality} 
                  onChange={(e) => setProfileForm(p => ({ ...p, residentCountry: e.target.value }))}
                  placeholder="Country of residence"
                />
              </div>
              <div className="form-group">
                <label className="form-label">Current City</label>
                <input 
                  type="text"
                  style={{ ...formInputBase, ...viewModeField }} 
                  value={profileForm.currentCity || ''} 
                  disabled={!editingSections.nationality} 
                  onChange={(e) => setProfileForm(p => ({ ...p, currentCity: e.target.value }))}
                  placeholder="e.g., Nairobi"
                />
              </div>
              <div className="form-group">
                <label className="form-label">Postal Code</label>
                <input 
                  type="text"
                  style={{ ...formInputBase, ...viewModeField }} 
                  value={profileForm.postalCode || ''} 
                  disabled={!editingSections.nationality} 
                  onChange={(e) => setProfileForm(p => ({ ...p, postalCode: e.target.value }))}
                  placeholder="Postal code"
                />
              </div>
            </div>
            
            <div className="profile-form-grid" style={formGridStyle('1fr')}>
              <div className="form-group">
                <label className="form-label">Full Address</label>
                <input 
                  type="text"
                  style={{ ...formInputBase, ...viewModeField }} 
                  value={profileForm.address || ''} 
                  disabled={!editingSections.nationality} 
                  onChange={(e) => setProfileForm(p => ({ ...p, address: e.target.value }))}
                  placeholder="Street address"
                />
              </div>
              <div style={{ gridColumn: '1 / -1', marginBottom: '15px' }}>
                <GoogleMapEmbed
                  address={mapQuery}
                  latitude={profileForm.latitude}
                  longitude={profileForm.longitude}
                  height={260}
                  label="Location Preview"
                />
              </div>
              <div className="form-group">
                <label className="form-label">Valid Work Documents</label>
                <input 
                  type="text"
                  style={{ ...formInputBase, ...viewModeField }} 
                  value={profileForm.validDocs || ''} 
                  disabled={!editingSections.nationality} 
                  onChange={(e) => setProfileForm(p => ({ ...p, validDocs: e.target.value }))}
                  placeholder="e.g., Passport, Driver's License, Work Permit"
                />
              </div>
            </div>
          </div>

          {/* Section 3: Preferred Locations */}
          <div className="card" style={elevatedCardStyle} data-section="locations">
            <div className="card-header" style={sectionCardHeaderStyle}>
              <h3 className="card-title"><FontAwesomeIcon icon={faMapMarked} /> Preferred Internship Locations</h3>
              <div className="card-header-actions" style={{ display: 'flex', gap: '8px' }}>
                {!editingSections.locations ? (
                  <button onClick={() => toggleSectionEdit('locations')} style={{ background: 'linear-gradient(135deg, #f97316 0%, #0d9488 100%)', color: 'white', border: 'none', padding: '8px 16px', borderRadius: '6px', fontSize: '14px', fontWeight: '600', cursor: 'pointer' }}>
                    <FontAwesomeIcon icon={faEdit} /> Edit
                  </button>
                ) : (
                  <>
                    <button onClick={() => saveSection('locations')} disabled={saving} style={{ background: '#10b981', color: 'white', border: 'none', padding: '8px 16px', borderRadius: '6px', fontSize: '14px', fontWeight: '600' }}>
                      <FontAwesomeIcon icon={faSave} /> {saving ? 'Saving...' : 'Save'}
                    </button>
                    <button onClick={() => toggleSectionEdit('locations')} style={{ background: '#dc2626', color: 'white', border: 'none', padding: '8px 16px', borderRadius: '6px', fontSize: '14px', fontWeight: '600' }}>
                      <FontAwesomeIcon icon={faTimes} /> Cancel
                    </button>
                  </>
                )}
              </div>
            </div>
            <div className="profile-form-grid" style={formGridStyle('repeat(3, 1fr)')}>
              <div className="form-group">
                <label className="form-label">Preferred Location 1</label>
                <input type="text" style={{ ...formInputBase, ...viewModeField }} value={profileForm.preferredLocation1 || ''} disabled={!editingSections.locations} onChange={(e) => setProfileForm(p => ({ ...p, preferredLocation1: e.target.value }))} placeholder="Country" />
              </div>
              <div className="form-group">
                <label className="form-label">Preferred Location 2</label>
                <input type="text" style={{ ...formInputBase, ...viewModeField }} value={profileForm.preferredLocation2 || ''} disabled={!editingSections.locations} onChange={(e) => setProfileForm(p => ({ ...p, preferredLocation2: e.target.value }))} placeholder="Country" />
              </div>
              <div className="form-group">
                <label className="form-label">Preferred Location 3</label>
                <input type="text" style={{ ...formInputBase, ...viewModeField }} value={profileForm.preferredLocation3 || ''} disabled={!editingSections.locations} onChange={(e) => setProfileForm(p => ({ ...p, preferredLocation3: e.target.value }))} placeholder="Country" />
              </div>
            </div>
            <div className="profile-form-grid" style={formGridStyle('repeat(2, 1fr)')}>
              <div className="form-group">
                <label className="form-label">Willing to Relocate for Internship?</label>
                <select style={{ ...formInputBase, ...viewModeField }} value={profileForm.willingToRelocate || ''} disabled={!editingSections.locations} onChange={(e) => setProfileForm(p => ({ ...p, willingToRelocate: e.target.value }))}>
                  <option value="">Select...</option>
                  <option value="yes">Yes, anywhere</option>
                  <option value="within-country">Within my country only</option>
                  <option value="no">No, local only</option>
                </select>
              </div>
              <div className="form-group">
                <label className="form-label">Internship Mode Preference</label>
                <select style={{ ...formInputBase, ...viewModeField }} value={profileForm.internshipMode || ''} disabled={!editingSections.locations} onChange={(e) => setProfileForm(p => ({ ...p, internshipMode: e.target.value }))}>
                  <option value="">Select preference</option>
                  <option value="onsite">On-site</option>
                  <option value="remote">Remote</option>
                  <option value="hybrid">Hybrid</option>
                  <option value="flexible">Flexible/Open to all</option>
                </select>
              </div>
            </div>
          </div>

          {/* Section 4: Education */}
          <div className="card" style={elevatedCardStyle} data-section="education">
            <div className="card-header" style={sectionCardHeaderStyle}>
              <h3 className="card-title"><FontAwesomeIcon icon={faGraduationCap} /> Education</h3>
              <div className="card-header-actions" style={{ display: 'flex', gap: '8px' }}>
                {!editingSections.education ? (
                  <button onClick={() => toggleSectionEdit('education')} style={{ background: 'linear-gradient(135deg, #f97316 0%, #0d9488 100%)', color: 'white', border: 'none', padding: '8px 16px', borderRadius: '6px', fontSize: '14px', fontWeight: '600', cursor: 'pointer' }}>
                    <FontAwesomeIcon icon={faEdit} /> Edit
                  </button>
                ) : (
                  <>
                    <button onClick={() => saveSection('education')} disabled={saving} style={{ background: '#10b981', color: 'white', border: 'none', padding: '8px 16px', borderRadius: '6px', fontSize: '14px', fontWeight: '600' }}>
                      <FontAwesomeIcon icon={faSave} /> {saving ? 'Saving...' : 'Save'}
                    </button>
                    <button onClick={() => toggleSectionEdit('education')} style={{ background: '#dc2626', color: 'white', border: 'none', padding: '8px 16px', borderRadius: '6px', fontSize: '14px', fontWeight: '600' }}>
                      <FontAwesomeIcon icon={faTimes} /> Cancel
                    </button>
                  </>
                )}
              </div>
            </div>
            <div style={{ marginBottom: '20px' }}>
              <label className="form-label">Academic Level</label>
              <select style={{ ...formInputBase, ...viewModeField, maxWidth: '400px' }} value={profileForm.academicLevel || ''} disabled={!editingSections.education} onChange={(e) => setProfileForm(p => ({ ...p, academicLevel: e.target.value }))}>
                <option value="">Select...</option>
                <option value="high-school">High School Student</option>
                <option value="undergraduate">Undergraduate/Bachelor's Student</option>
                <option value="graduate">Graduate/Master's Student</option>
                <option value="phd">PhD Student</option>
                <option value="recent-graduate">Recent Graduate (within 6 months)</option>
                {/* Legacy values for backward compatibility */}
                <option value="High School Student">High School Student</option>
                <option value="Undergraduate - Freshman">Undergraduate - Freshman</option>
                <option value="Undergraduate - Sophomore">Undergraduate - Sophomore</option>
                <option value="Undergraduate - Junior">Undergraduate - Junior</option>
                <option value="Undergraduate - Senior">Undergraduate - Senior</option>
                <option value="Graduate/Master's">Graduate/Master's</option>
                <option value="PhD Student">PhD Student</option>
                <option value="Recent Graduate">Recent Graduate</option>
                <option value="Diploma Student">Diploma Student</option>
                <option value="Certificate Student">Certificate Student</option>
              </select>
            </div>
            {educationEntries.map((edu, idx) => (
              <div key={idx} style={{ padding: '20px', background: '#f9fafb', borderRadius: '12px', marginBottom: '15px' }}>
                <div className="profile-form-grid" style={formGridStyle('repeat(2, 1fr)')}>
                  <div className="form-group">
                    <label className="form-label">Institution</label>
                    <input type="text" style={{ ...formInputBase, ...viewModeField }} value={edu.institution || ''} disabled={!editingSections.education} onChange={(e) => { const newEdu = [...educationEntries]; newEdu[idx] = {...newEdu[idx], institution: e.target.value}; setEducationEntries(newEdu); }} placeholder="University name" />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Degree/Program</label>
                    <input type="text" style={{ ...formInputBase, ...viewModeField }} value={edu.degree || ''} disabled={!editingSections.education} onChange={(e) => { const newEdu = [...educationEntries]; newEdu[idx] = {...newEdu[idx], degree: e.target.value}; setEducationEntries(newEdu); }} placeholder="e.g., Bachelor of Science" />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Field of Study</label>
                    <input type="text" style={{ ...formInputBase, ...viewModeField }} value={edu.fieldOfStudy || ''} disabled={!editingSections.education} onChange={(e) => { const newEdu = [...educationEntries]; newEdu[idx] = {...newEdu[idx], fieldOfStudy: e.target.value}; setEducationEntries(newEdu); }} placeholder="e.g., Computer Science" />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Start Year</label>
                    <input type="text" style={{ ...formInputBase, ...viewModeField }} value={edu.startYear || edu.startDate || ''} disabled={!editingSections.education} onChange={(e) => { const newEdu = [...educationEntries]; newEdu[idx] = {...newEdu[idx], startYear: e.target.value}; setEducationEntries(newEdu); }} placeholder="e.g., 2020" />
                  </div>
                  <div className="form-group">
                    <label className="form-label">End Year</label>
                    <input type="text" style={{ ...formInputBase, ...viewModeField }} value={edu.endYear || edu.endDate || ''} disabled={!editingSections.education} onChange={(e) => { const newEdu = [...educationEntries]; newEdu[idx] = {...newEdu[idx], endYear: e.target.value}; setEducationEntries(newEdu); }} placeholder="e.g., 2024 or Expected 2025" />
                  </div>
                  <div className="form-group">
                    <label className="form-label">GPA/Grade</label>
                    <input type="text" style={{ ...formInputBase, ...viewModeField }} value={edu.gpa || ''} disabled={!editingSections.education} onChange={(e) => { const newEdu = [...educationEntries]; newEdu[idx] = {...newEdu[idx], gpa: e.target.value}; setEducationEntries(newEdu); }} placeholder="e.g., 3.8/4.0" />
                  </div>
                </div>
                <div className="profile-form-grid" style={formGridStyle('1fr', { marginTop: '10px' })}>
                  <div className="form-group">
                    <label className="form-label">Relevant Coursework</label>
                    <textarea style={{ ...formInputBase, ...viewModeField, minHeight: '60px' }} value={edu.coursework || edu.relevantCoursework || ''} disabled={!editingSections.education} onChange={(e) => { const newEdu = [...educationEntries]; newEdu[idx] = {...newEdu[idx], coursework: e.target.value}; setEducationEntries(newEdu); }} placeholder="List relevant courses" />
                  </div>
                </div>
                {editingSections.education && educationEntries.length > 1 && (
                  <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '12px' }}>
                    <button
                      onClick={() => setEducationEntries(educationEntries.filter((_, i) => i !== idx))}
                      style={{
                        background: 'rgba(220,38,38,0.1)',
                        color: '#b91c1c',
                        border: '1px solid rgba(220,38,38,0.2)',
                        borderRadius: '8px',
                        padding: '8px 14px',
                        cursor: 'pointer',
                        fontWeight: '600',
                        fontSize: '13px',
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '6px'
                      }}
                    >
                      <FontAwesomeIcon icon={faTrash} />
                      Remove Education
                    </button>
                  </div>
                )}
              </div>
            ))}
            {educationEntries.length === 0 && <div style={{ color: '#999', padding: '20px', textAlign: 'center' }}>No education entries added</div>}
            {editingSections.education && (
              <button onClick={() => setEducationEntries([...educationEntries, { institution: '', degree: '', fieldOfStudy: '', location: '', gpa: '', startYear: '', endYear: '', currentYear: '', coursework: '', achievements: '' }])} style={{ background: 'linear-gradient(135deg, #f97316 0%, #0d9488 100%)', color: 'white', border: 'none', padding: '12px 24px', borderRadius: '8px', cursor: 'pointer', fontWeight: '600', fontSize: '14px', marginTop: '10px' }}>
                + Add Education
              </button>
            )}
          </div>

          {/* Section 5: Objective */}
          <div className="card" style={elevatedCardStyle} data-section="objective">
            <div className="card-header" style={sectionCardHeaderStyle}>
              <h3 className="card-title"><FontAwesomeIcon icon={faBullseye} /> Internship Objective & Career Goals</h3>
              <div className="card-header-actions" style={{ display: 'flex', gap: '8px' }}>
                {!editingSections.objective ? (
                  <button onClick={() => toggleSectionEdit('objective')} style={{ background: 'linear-gradient(135deg, #f97316 0%, #0d9488 100%)', color: 'white', border: 'none', padding: '8px 16px', borderRadius: '6px', fontSize: '14px', fontWeight: '600', cursor: 'pointer' }}>
                    <FontAwesomeIcon icon={faEdit} /> Edit
                  </button>
                ) : (
                  <>
                    <button onClick={() => saveSection('objective')} disabled={saving} style={{ background: '#10b981', color: 'white', border: 'none', padding: '8px 16px', borderRadius: '6px', fontSize: '14px', fontWeight: '600' }}>
                      <FontAwesomeIcon icon={faSave} /> {saving ? 'Saving...' : 'Save'}
                    </button>
                    <button onClick={() => toggleSectionEdit('objective')} style={{ background: '#dc2626', color: 'white', border: 'none', padding: '8px 16px', borderRadius: '6px', fontSize: '14px', fontWeight: '600' }}>
                      <FontAwesomeIcon icon={faTimes} /> Cancel
                    </button>
                  </>
                )}
              </div>
            </div>
            <div style={{ marginBottom: '20px' }}>
              <label className="form-label">Professional Objective</label>
              <textarea style={{ ...formInputBase, ...viewModeField, minHeight: '120px', width: '100%', fontFamily: 'inherit' }} value={profileForm.objective || ''} disabled={!editingSections.objective} onChange={(e) => setProfileForm(p => ({ ...p, objective: e.target.value }))} placeholder="Describe your career goals and what you hope to achieve through an internship" />
            </div>
            <div className="profile-form-grid" style={formGridStyle('repeat(2, 1fr)')}>
              <div className="form-group">
                <label className="form-label">Industry of Interest</label>
                <input type="text" style={{ ...formInputBase, ...viewModeField }} value={profileForm.industryInterest || ''} disabled={!editingSections.objective} onChange={(e) => setProfileForm(p => ({ ...p, industryInterest: e.target.value }))} placeholder="e.g., Technology, Finance" />
              </div>
              <div className="form-group">
                <label className="form-label">Preferred Internship Title</label>
                <input type="text" style={{ ...formInputBase, ...viewModeField }} value={profileForm.preferredRole || ''} disabled={!editingSections.objective} onChange={(e) => setProfileForm(p => ({ ...p, preferredRole: e.target.value }))} placeholder="e.g., Software Developer Intern, Marketing Intern" />
                {!editingSections.objective && (
                  <small style={{ color: '#64748b', fontSize: '0.875rem', marginTop: '4px', display: 'block' }}>
                    Internships matching this title will be shown to you
                  </small>
                )}
              </div>
            </div>
            <div style={{ marginTop: '20px' }}>
              <label className="form-label">Career Interests</label>
              {editingSections.objective && (
                <div style={{ display: 'flex', gap: '10px', marginTop: '10px', marginBottom: '15px' }}>
                  <input 
                    type="text" 
                    style={{ ...formInputBase, flex: 1 }}
                    placeholder="e.g., Software Development, Data Analysis, Marketing"
                    value={careerInterestInput}
                    onChange={(e) => setCareerInterestInput(e.target.value)}
                    onKeyPress={(e) => {
                      if (e.key === 'Enter' && careerInterestInput.trim()) {
                        setCareerInterests([...careerInterests, careerInterestInput.trim()]);
                        setCareerInterestInput('');
                      }
                    }}
                  />
                  <button 
                    onClick={() => {
                      if (careerInterestInput.trim()) {
                        setCareerInterests([...careerInterests, careerInterestInput.trim()]);
                        setCareerInterestInput('');
                      }
                    }}
                    style={{ 
                      background: 'linear-gradient(135deg, #f97316 0%, #0d9488 100%)', 
                      color: 'white', 
                      border: 'none', 
                      padding: '12px 24px', 
                      borderRadius: '8px', 
                      cursor: 'pointer', 
                      fontWeight: '600', 
                      fontSize: '14px',
                      whiteSpace: 'nowrap'
                    }}
                  >
                    + Add
                  </button>
                </div>
              )}
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px', marginTop: '10px' }}>
                {careerInterests.map((interest, idx) => (
                  <div key={idx} style={{ background: '#f0fdf4', color: '#16a34a', padding: '6px 12px', borderRadius: '16px', fontSize: '14px', display: 'flex', alignItems: 'center', gap: '8px', border: '1px solid #bbf7d0' }}>
                    {interest}
                    {editingSections.objective && (
                      <button onClick={() => setCareerInterests(careerInterests.filter((_, i) => i !== idx))} style={{ background: 'none', border: 'none', color: '#dc2626', cursor: 'pointer', padding: 0, fontSize: '16px' }}>×</button>
                    )}
                  </div>
                ))}
                {careerInterests.length === 0 && <span style={{ color: '#999' }}>No interests added</span>}
              </div>
            </div>
          </div>

          {/* Section 6: Experience */}
          <div className="card" style={elevatedCardStyle} data-section="experience">
            <div className="card-header" style={sectionCardHeaderStyle}>
              <h3 className="card-title"><FontAwesomeIcon icon={faBriefcase} /> Previous Internships & Work Experience</h3>
              <div className="card-header-actions" style={{ display: 'flex', gap: '8px' }}>
                {!editingSections.experience ? (
                  <button onClick={() => toggleSectionEdit('experience')} style={{ background: 'linear-gradient(135deg, #f97316 0%, #0d9488 100%)', color: 'white', border: 'none', padding: '8px 16px', borderRadius: '6px', fontSize: '14px', fontWeight: '600', cursor: 'pointer' }}>
                    <FontAwesomeIcon icon={faEdit} /> Edit
                  </button>
                ) : (
                  <>
                    <button onClick={() => saveSection('experience')} disabled={saving} style={{ background: '#10b981', color: 'white', border: 'none', padding: '8px 16px', borderRadius: '6px', fontSize: '14px', fontWeight: '600' }}>
                      <FontAwesomeIcon icon={faSave} /> {saving ? 'Saving...' : 'Save'}
                    </button>
                    <button onClick={() => toggleSectionEdit('experience')} style={{ background: '#dc2626', color: 'white', border: 'none', padding: '8px 16px', borderRadius: '6px', fontSize: '14px', fontWeight: '600' }}>
                      <FontAwesomeIcon icon={faTimes} /> Cancel
                    </button>
                  </>
                )}
              </div>
            </div>
            {experienceEntries.map((exp, idx) => (
              <div key={idx} style={{ padding: '20px', background: '#f9fafb', borderRadius: '12px', marginBottom: '15px' }}>
                <div className="profile-form-grid" style={formGridStyle('repeat(2, 1fr)')}>
                  <div className="form-group">
                    <label className="form-label">Position Title</label>
                    <input type="text" style={{ ...formInputBase, ...viewModeField }} value={exp.positionTitle || exp.title || ''} disabled={!editingSections.experience} onChange={(e) => { const newExp = [...experienceEntries]; newExp[idx] = {...newExp[idx], positionTitle: e.target.value}; setExperienceEntries(newExp); }} placeholder="e.g., Marketing Intern" />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Company</label>
                    <input type="text" style={{ ...formInputBase, ...viewModeField }} value={exp.company || ''} disabled={!editingSections.experience} onChange={(e) => { const newExp = [...experienceEntries]; newExp[idx] = {...newExp[idx], company: e.target.value}; setExperienceEntries(newExp); }} placeholder="Company name" />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Location</label>
                    <input type="text" style={{ ...formInputBase, ...viewModeField }} value={exp.location || ''} disabled={!editingSections.experience} onChange={(e) => { const newExp = [...experienceEntries]; newExp[idx] = {...newExp[idx], location: e.target.value}; setExperienceEntries(newExp); }} placeholder="City, Country" />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Type</label>
                    <select style={{ ...formInputBase, ...viewModeField }} value={exp.experienceType || exp.internshipType || 'internship'} disabled={!editingSections.experience} onChange={(e) => { const newExp = [...experienceEntries]; newExp[idx] = {...newExp[idx], experienceType: e.target.value}; setExperienceEntries(newExp); }}>
                      <option value="internship">Internship</option>
                      <option value="part-time">Part-time Work</option>
                      <option value="volunteer">Volunteer</option>
                      <option value="freelance">Freelance</option>
                      <option value="full-time">Full-time</option>
                    </select>
                  </div>
                  <div className="form-group">
                    <label className="form-label">Start Date</label>
                    <input type="month" style={{ ...formInputBase, ...viewModeField }} value={exp.startDate || ''} disabled={!editingSections.experience} onChange={(e) => { const newExp = [...experienceEntries]; newExp[idx] = {...newExp[idx], startDate: e.target.value}; setExperienceEntries(newExp); }} />
                  </div>
                  <div className="form-group">
                    <label className="form-label">End Date</label>
                    <input type="month" style={{ ...formInputBase, ...viewModeField }} value={exp.endDate || ''} disabled={!editingSections.experience || exp.currentJob || exp.currentlyWorking} onChange={(e) => { const newExp = [...experienceEntries]; newExp[idx] = {...newExp[idx], endDate: e.target.value}; setExperienceEntries(newExp); }} />
                  </div>
                </div>
                <div style={{ marginTop: '15px' }}>
                  <label className="form-label">Description & Responsibilities</label>
                  <textarea style={{ ...formInputBase, ...viewModeField, minHeight: '80px', width: '100%', fontFamily: 'inherit', whiteSpace: 'pre-wrap' }} value={exp.responsibilities || exp.description || exp.jobDescription || ''} disabled={!editingSections.experience} onChange={(e) => { const newExp = [...experienceEntries]; newExp[idx] = {...newExp[idx], responsibilities: e.target.value, description: e.target.value, jobDescription: e.target.value}; setExperienceEntries(newExp); }} placeholder="Describe your responsibilities" />
                </div>
                {editingSections.experience && experienceEntries.length > 1 && (
                  <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '12px' }}>
                    <button
                      onClick={() => setExperienceEntries(experienceEntries.filter((_, i) => i !== idx))}
                      style={{
                        background: 'rgba(220,38,38,0.1)',
                        color: '#b91c1c',
                        border: '1px solid rgba(220,38,38,0.2)',
                        borderRadius: '8px',
                        padding: '8px 14px',
                        cursor: 'pointer',
                        fontWeight: '600',
                        fontSize: '13px',
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '6px'
                      }}
                    >
                      <FontAwesomeIcon icon={faTrash} />
                      Remove Experience
                    </button>
                  </div>
                )}
              </div>
            ))}
            {experienceEntries.length === 0 && <div style={{ color: '#999', padding: '20px', textAlign: 'center' }}>No experience entries added</div>}
            {editingSections.experience && (
              <button onClick={() => setExperienceEntries([...experienceEntries, { positionTitle: '', company: '', location: '', experienceType: 'internship', startDate: '', endDate: '', currentlyWorking: false, responsibilities: '' }])} style={{ background: 'linear-gradient(135deg, #f97316 0%, #0d9488 100%)', color: 'white', border: 'none', padding: '12px 24px', borderRadius: '8px', cursor: 'pointer', fontWeight: '600', fontSize: '14px', marginTop: '10px' }}>
                + Add Experience
              </button>
            )}
          </div>

          {/* Section 7: Skills */}
          <div className="card" style={elevatedCardStyle} data-section="skills">
            <div className="card-header" style={sectionCardHeaderStyle}>
              <h3 className="card-title"><FontAwesomeIcon icon={faLightbulb} /> Skills & Competencies</h3>
              <div className="card-header-actions" style={{ display: 'flex', gap: '8px' }}>
                {!editingSections.skills ? (
                  <button onClick={() => toggleSectionEdit('skills')} style={{ background: 'linear-gradient(135deg, #f97316 0%, #0d9488 100%)', color: 'white', border: 'none', padding: '8px 16px', borderRadius: '6px', fontSize: '14px', fontWeight: '600', cursor: 'pointer' }}>
                    <FontAwesomeIcon icon={faEdit} /> Edit
                  </button>
                ) : (
                  <>
                    <button onClick={() => saveSection('skills')} disabled={saving} style={{ background: '#10b981', color: 'white', border: 'none', padding: '8px 16px', borderRadius: '6px', fontSize: '14px', fontWeight: '600' }}>
                      <FontAwesomeIcon icon={faSave} /> {saving ? 'Saving...' : 'Save'}
                    </button>
                    <button onClick={() => toggleSectionEdit('skills')} style={{ background: '#dc2626', color: 'white', border: 'none', padding: '8px 16px', borderRadius: '6px', fontSize: '14px', fontWeight: '600' }}>
                      <FontAwesomeIcon icon={faTimes} /> Cancel
                    </button>
                  </>
                )}
              </div>
            </div>
            <div style={{ marginBottom: '30px' }}>
              <label className="form-label" style={{ marginBottom: '8px', display: 'block', fontWeight: '600', color: '#1f2937' }}>
                Technical Skills <span style={{ color: '#dc2626' }}>*</span>
              </label>
              <div style={{ 
                background: 'linear-gradient(135deg, #fef3c7 0%, #dbeafe 100%)', 
                padding: '12px 16px', 
                borderRadius: '8px', 
                marginBottom: '12px',
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
              }}>
                <FontAwesomeIcon icon={faInfoCircle} style={{ color: '#0d9488', fontSize: '16px' }} />
                <span style={{ color: '#475569', fontSize: '14px' }}>
                  Add programming languages, software, tools, and technical abilities.
                </span>
              </div>
              <div style={{ display: 'flex', gap: '10px', alignItems: 'center', marginBottom: '15px', position: 'relative' }}>
                <div style={{ flex: 1, position: 'relative' }}>
                  <input
                    type="text"
                    list="technicalSkillsList"
                    value={technicalSkillInput}
                    onChange={(e) => setTechnicalSkillInput(e.target.value)}
                    onKeyPress={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        if (technicalSkillInput.trim() && !technicalSkills.includes(technicalSkillInput.trim())) {
                          setTechnicalSkills([...technicalSkills, technicalSkillInput.trim()]);
                          setTechnicalSkillInput('');
                        }
                      }
                    }}
                    placeholder="Type or select a technical skill"
                    style={{
                      width: '100%',
                      padding: '12px 16px',
                      border: '2px solid #e0e0e0',
                      borderRadius: '8px',
                      fontSize: '14px',
                      outline: 'none',
                      transition: 'border-color 0.2s',
                      backgroundColor: editingSections.skills ? '#ffffff' : '#f7f9fc'
                    }}
                    disabled={!editingSections.skills}
                  />
                  <datalist id="technicalSkillsList">
                    {Array.isArray(globalSkillsData?.skills) && globalSkillsData.skills.map((skill, idx) => (
                      <option key={idx} value={skill} />
                    ))}
                  </datalist>
                </div>
                {editingSections.skills && (
                  <button
                    onClick={() => {
                      if (technicalSkillInput.trim() && !technicalSkills.includes(technicalSkillInput.trim())) {
                        setTechnicalSkills([...technicalSkills, technicalSkillInput.trim()]);
                        setTechnicalSkillInput('');
                      }
                    }}
                    style={{
                      background: 'linear-gradient(135deg, #f97316 0%, #0d9488 100%)',
                      color: 'white',
                      border: 'none',
                      padding: '12px 24px',
                      borderRadius: '8px',
                      fontSize: '14px',
                      fontWeight: '600',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '6px',
                      whiteSpace: 'nowrap'
                    }}
                  >
                    <FontAwesomeIcon icon={faPlus} /> Add
                  </button>
                )}
              </div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px', marginTop: '10px' }}>
                {technicalSkills.map((skill, idx) => (
                  <div key={idx} style={{ 
                    background: '#fef3c7', 
                    color: '#d97706', 
                    padding: '8px 14px', 
                    borderRadius: '16px', 
                    fontSize: '14px', 
                    display: 'flex', 
                    alignItems: 'center', 
                    gap: '8px', 
                    border: '1px solid #fde68a',
                    fontWeight: '500'
                  }}>
                    {skill}
                    {editingSections.skills && (
                      <button 
                        onClick={() => setTechnicalSkills(technicalSkills.filter((_, i) => i !== idx))} 
                        style={{ 
                          background: 'none', 
                          border: 'none', 
                          color: '#dc2626', 
                          cursor: 'pointer', 
                          padding: 0, 
                          fontSize: '18px',
                          fontWeight: 'bold',
                          lineHeight: '1',
                          marginLeft: '4px'
                        }}
                      >
                        ×
                      </button>
                    )}
                  </div>
                ))}
                {technicalSkills.length === 0 && !editingSections.skills && (
                  <span style={{ color: '#999', fontStyle: 'italic' }}>No technical skills added</span>
                )}
              </div>
            </div>
            <div>
              <label className="form-label" style={{ marginBottom: '8px', display: 'block', fontWeight: '600', color: '#1f2937' }}>
                Soft Skills
              </label>
              <div style={{ 
                background: 'linear-gradient(135deg, #fef3c7 0%, #dbeafe 100%)', 
                padding: '12px 16px', 
                borderRadius: '8px', 
                marginBottom: '12px',
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
              }}>
                <FontAwesomeIcon icon={faInfoCircle} style={{ color: '#0d9488', fontSize: '16px' }} />
                <span style={{ color: '#475569', fontSize: '14px' }}>
                  Add communication, leadership, teamwork, and other interpersonal skills.
                </span>
              </div>
              <div style={{ display: 'flex', gap: '10px', alignItems: 'center', marginBottom: '15px' }}>
                <input
                  type="text"
                  list="softSkillsListInternProfile"
                  value={softSkillInput}
                  onChange={(e) => setSoftSkillInput(e.target.value)}
                  onKeyPress={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      if (softSkillInput.trim() && !softSkills.includes(softSkillInput.trim())) {
                        setSoftSkills([...softSkills, softSkillInput.trim()]);
                        setSoftSkillInput('');
                      }
                    }
                  }}
                  placeholder="e.g., Communication, Leadership, Problem-solving"
                  style={{
                    flex: 1,
                    padding: '12px 16px',
                    border: '2px solid #e0e0e0',
                    borderRadius: '8px',
                    fontSize: '14px',
                    outline: 'none',
                    transition: 'border-color 0.2s',
                    backgroundColor: editingSections.skills ? '#ffffff' : '#f7f9fc'
                  }}
                  disabled={!editingSections.skills}
                />
                <datalist id="softSkillsListInternProfile">
                  {Array.isArray(softSkillsSuggestions) && softSkillsSuggestions.map((skill, idx) => (
                    <option key={idx} value={skill} />
                  ))}
                </datalist>
                {editingSections.skills && (
                  <button
                    onClick={() => {
                      if (softSkillInput.trim() && !softSkills.includes(softSkillInput.trim())) {
                        setSoftSkills([...softSkills, softSkillInput.trim()]);
                        setSoftSkillInput('');
                      }
                    }}
                    style={{
                      background: 'linear-gradient(135deg, #f97316 0%, #0d9488 100%)',
                      color: 'white',
                      border: 'none',
                      padding: '12px 24px',
                      borderRadius: '8px',
                      fontSize: '14px',
                      fontWeight: '600',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '6px',
                      whiteSpace: 'nowrap'
                    }}
                  >
                    <FontAwesomeIcon icon={faPlus} /> Add
                  </button>
                )}
              </div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px', marginTop: '10px' }}>
                {softSkills.map((skill, idx) => (
                  <div key={idx} style={{ 
                    background: '#dbeafe', 
                    color: '#1e40af', 
                    padding: '8px 14px', 
                    borderRadius: '16px', 
                    fontSize: '14px', 
                    display: 'flex', 
                    alignItems: 'center', 
                    gap: '8px', 
                    border: '1px solid #bfdbfe',
                    fontWeight: '500'
                  }}>
                    {skill}
                    {editingSections.skills && (
                      <button 
                        onClick={() => setSoftSkills(softSkills.filter((_, i) => i !== idx))} 
                        style={{ 
                          background: 'none', 
                          border: 'none', 
                          color: '#dc2626', 
                          cursor: 'pointer', 
                          padding: 0, 
                          fontSize: '18px',
                          fontWeight: 'bold',
                          lineHeight: '1',
                          marginLeft: '4px'
                        }}
                      >
                        ×
                      </button>
                    )}
                  </div>
                ))}
                {softSkills.length === 0 && !editingSections.skills && (
                  <span style={{ color: '#999', fontStyle: 'italic' }}>No soft skills added</span>
                )}
              </div>
            </div>
          </div>

          {/* Section 8: Languages */}
          <div className="card" style={elevatedCardStyle} data-section="languages">
            <div className="card-header" style={sectionCardHeaderStyle}>
              <h3 className="card-title"><FontAwesomeIcon icon={faLanguage} /> Languages</h3>
              <div className="card-header-actions" style={{ display: 'flex', gap: '8px' }}>
                {!editingSections.languages ? (
                  <button onClick={() => toggleSectionEdit('languages')} style={{ background: 'linear-gradient(135deg, #f97316 0%, #0d9488 100%)', color: 'white', border: 'none', padding: '8px 16px', borderRadius: '6px', fontSize: '14px', fontWeight: '600', cursor: 'pointer' }}>
                    <FontAwesomeIcon icon={faEdit} /> Edit
                  </button>
                ) : (
                  <>
                    <button onClick={() => saveSection('languages')} disabled={saving} style={{ background: '#10b981', color: 'white', border: 'none', padding: '8px 16px', borderRadius: '6px', fontSize: '14px', fontWeight: '600' }}>
                      <FontAwesomeIcon icon={faSave} /> {saving ? 'Saving...' : 'Save'}
                    </button>
                    <button onClick={() => toggleSectionEdit('languages')} style={{ background: '#dc2626', color: 'white', border: 'none', padding: '8px 16px', borderRadius: '6px', fontSize: '14px', fontWeight: '600' }}>
                      <FontAwesomeIcon icon={faTimes} /> Cancel
                    </button>
                  </>
                )}
              </div>
            </div>
            {languages.map((lang, idx) => (
              <div key={idx} style={{ padding: '15px', background: '#f9fafb', borderRadius: '8px', marginBottom: '10px', position: 'relative' }}>
                {editingSections.languages && (
                  <button onClick={() => setLanguages(languages.filter((_, i) => i !== idx))} style={{ position: 'absolute', top: '10px', right: '10px', background: '#dc2626', border: 'none', borderRadius: '50%', width: '30px', height: '30px', color: 'white', cursor: 'pointer' }}>×</button>
                )}
                {editingSections.languages ? (
                  <div className="profile-form-grid" style={formGridStyle('1fr 1fr', { paddingRight: isMobileView ? 0 : '40px' })}>
                    <div>
                      <label className="form-label">Language</label>
                      <input type="text" style={{ ...formInputBase, width: '100%' }} value={lang.language || ''} onChange={(e) => { const newLangs = [...languages]; newLangs[idx] = {...newLangs[idx], language: e.target.value}; setLanguages(newLangs); }} placeholder="e.g., English" />
                    </div>
                    <div>
                      <label className="form-label">Proficiency</label>
                      <select style={{ ...formInputBase, width: '100%' }} value={lang.proficiency || ''} onChange={(e) => { const newLangs = [...languages]; newLangs[idx] = {...newLangs[idx], proficiency: e.target.value}; setLanguages(newLangs); }}>
                        <option value="">Select Level</option>
                        <option value="native">Native</option>
                        <option value="fluent">Fluent</option>
                        <option value="advanced">Advanced</option>
                        <option value="intermediate">Intermediate</option>
                        <option value="basic">Basic</option>
                      </select>
                    </div>
                  </div>
                ) : (
                  <>
                    <h4 style={{ marginBottom: '5px', fontSize: '16px', fontWeight: '600' }}>{lang.language || ''}</h4>
                    <p style={{ color: '#666', margin: 0 }}>{lang.proficiency || ''}</p>
                  </>
                )}
              </div>
            ))}
            {languages.length === 0 && <div style={{ color: '#999', padding: '20px', textAlign: 'center' }}>No languages added</div>}
            {editingSections.languages && (
              <button onClick={() => setLanguages([...languages, { language: '', proficiency: '' }])} style={{ background: 'linear-gradient(135deg, #f97316 0%, #0d9488 100%)', color: 'white', border: 'none', padding: '12px 24px', borderRadius: '8px', cursor: 'pointer', fontWeight: '600', fontSize: '14px', marginTop: '10px' }}>
                + Add Language
              </button>
            )}
          </div>

          {/* Section 9: Projects */}
          <div className="card" style={elevatedCardStyle} data-section="projects">
            <div className="card-header" style={sectionCardHeaderStyle}>
              <h3 className="card-title"><FontAwesomeIcon icon={faProjectDiagram} /> Academic Projects & Portfolio</h3>
              <div className="card-header-actions" style={{ display: 'flex', gap: '8px' }}>
                {!editingSections.projects ? (
                  <button onClick={() => toggleSectionEdit('projects')} style={{ background: 'linear-gradient(135deg, #f97316 0%, #0d9488 100%)', color: 'white', border: 'none', padding: '8px 16px', borderRadius: '6px', fontSize: '14px', fontWeight: '600', cursor: 'pointer' }}>
                    <FontAwesomeIcon icon={faEdit} /> Edit
                  </button>
                ) : (
                  <>
                    <button onClick={() => saveSection('projects')} disabled={saving} style={{ background: '#10b981', color: 'white', border: 'none', padding: '8px 16px', borderRadius: '6px', fontSize: '14px', fontWeight: '600' }}>
                      <FontAwesomeIcon icon={faSave} /> {saving ? 'Saving...' : 'Save'}
                    </button>
                    <button onClick={() => toggleSectionEdit('projects')} style={{ background: '#dc2626', color: 'white', border: 'none', padding: '8px 16px', borderRadius: '6px', fontSize: '14px', fontWeight: '600' }}>
                      <FontAwesomeIcon icon={faTimes} /> Cancel
                    </button>
                  </>
                )}
              </div>
            </div>
            {projectEntries.map((proj, idx) => (
              <div key={idx} style={{ padding: '20px', background: '#f9fafb', borderRadius: '12px', marginBottom: '15px', position: 'relative' }}>
                {editingSections.projects && (
                  <button onClick={() => setProjectEntries(projectEntries.filter((_, i) => i !== idx))} style={{ position: 'absolute', top: '10px', right: '10px', background: '#dc2626', border: 'none', borderRadius: '50%', width: '30px', height: '30px', color: 'white', cursor: 'pointer' }}>×</button>
                )}
                <div className="profile-form-grid" style={formGridStyle('repeat(2, 1fr)')}>
                  <div className="form-group">
                    <label className="form-label">Project Title</label>
                    <input type="text" style={{ ...formInputBase, ...viewModeField }} value={proj.projectTitle || proj.title || ''} disabled={!editingSections.projects} onChange={(e) => { const newProj = [...projectEntries]; newProj[idx] = {...newProj[idx], projectTitle: e.target.value}; setProjectEntries(newProj); }} placeholder="Project name" />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Project Type</label>
                    <input type="text" style={{ ...formInputBase, ...viewModeField }} value={proj.projectType || proj.category || ''} disabled={!editingSections.projects} onChange={(e) => { const newProj = [...projectEntries]; newProj[idx] = {...newProj[idx], projectType: e.target.value}; setProjectEntries(newProj); }} placeholder="e.g., Course, Personal, Professional" />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Your Role</label>
                    <input type="text" style={{ ...formInputBase, ...viewModeField }} value={proj.projectRole || proj.role || ''} disabled={!editingSections.projects} onChange={(e) => { const newProj = [...projectEntries]; newProj[idx] = {...newProj[idx], projectRole: e.target.value}; setProjectEntries(newProj); }} placeholder="e.g., Full-Stack Developer" />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Project Date</label>
                    <input type="text" style={{ ...formInputBase, ...viewModeField }} value={proj.projectDate || ''} disabled={!editingSections.projects} onChange={(e) => { const newProj = [...projectEntries]; newProj[idx] = {...newProj[idx], projectDate: e.target.value}; setProjectEntries(newProj); }} placeholder="e.g., Summer 2024" />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Project URL</label>
                    <input type="url" style={{ ...formInputBase, ...viewModeField }} value={proj.projectUrl || proj.url || ''} disabled={!editingSections.projects} onChange={(e) => { const newProj = [...projectEntries]; newProj[idx] = {...newProj[idx], projectUrl: e.target.value}; setProjectEntries(newProj); }} placeholder="https://github.com/project" />
                  </div>
                </div>
                <div style={{ marginTop: '15px' }}>
                  <label className="form-label">Description</label>
                  <textarea style={{ ...formInputBase, ...viewModeField, minHeight: '80px', width: '100%', fontFamily: 'inherit' }} value={proj.projectDescription || proj.description || ''} disabled={!editingSections.projects} onChange={(e) => { const newProj = [...projectEntries]; newProj[idx] = {...newProj[idx], projectDescription: e.target.value}; setProjectEntries(newProj); }} placeholder="Describe the project" />
                </div>
              </div>
            ))}
            {projectEntries.length === 0 && <div style={{ color: '#999', padding: '20px', textAlign: 'center' }}>No projects added</div>}
            {editingSections.projects && (
              <button onClick={() => setProjectEntries([...projectEntries, { projectTitle: '', projectType: '', projectRole: '', projectDate: '', projectUrl: '', projectDescription: '' }])} style={{ background: 'linear-gradient(135deg, #f97316 0%, #0d9488 100%)', color: 'white', border: 'none', padding: '12px 24px', borderRadius: '8px', cursor: 'pointer', fontWeight: '600', fontSize: '14px', marginTop: '10px' }}>
                + Add Project
              </button>
            )}
          </div>

          {/* Section 10: Activities */}
          <div className="card" style={elevatedCardStyle} data-section="activities">
            <div className="card-header" style={sectionCardHeaderStyle}>
              <h3 className="card-title"><FontAwesomeIcon icon={faUsers} /> Extracurricular Activities & Leadership</h3>
              <div className="card-header-actions" style={{ display: 'flex', gap: '8px' }}>
                {!editingSections.activities ? (
                  <button onClick={() => toggleSectionEdit('activities')} style={{ background: 'linear-gradient(135deg, #f97316 0%, #0d9488 100%)', color: 'white', border: 'none', padding: '8px 16px', borderRadius: '6px', fontSize: '14px', fontWeight: '600', cursor: 'pointer' }}>
                    <FontAwesomeIcon icon={faEdit} /> Edit
                  </button>
                ) : (
                  <>
                    <button onClick={() => saveSection('activities')} disabled={saving} style={{ background: '#10b981', color: 'white', border: 'none', padding: '8px 16px', borderRadius: '6px', fontSize: '14px', fontWeight: '600' }}>
                      <FontAwesomeIcon icon={faSave} /> {saving ? 'Saving...' : 'Save'}
                    </button>
                    <button onClick={() => toggleSectionEdit('activities')} style={{ background: '#dc2626', color: 'white', border: 'none', padding: '8px 16px', borderRadius: '6px', fontSize: '14px', fontWeight: '600' }}>
                      <FontAwesomeIcon icon={faTimes} /> Cancel
                    </button>
                  </>
                )}
              </div>
            </div>
            {activityEntries.map((act, idx) => (
              <div key={idx} style={{ padding: '20px', background: '#f9fafb', borderRadius: '12px', marginBottom: '15px', position: 'relative' }}>
                {editingSections.activities && (
                  <button onClick={() => setActivityEntries(activityEntries.filter((_, i) => i !== idx))} style={{ position: 'absolute', top: '10px', right: '10px', background: '#dc2626', border: 'none', borderRadius: '50%', width: '30px', height: '30px', color: 'white', cursor: 'pointer' }}>×</button>
                )}
                <div className="profile-form-grid" style={formGridStyle('repeat(2, 1fr)')}>
                  <div className="form-group">
                    <label className="form-label">Activity Name</label>
                    <input type="text" style={{ ...formInputBase, ...viewModeField }} value={act.activityName || act.name || ''} disabled={!editingSections.activities} onChange={(e) => { const newAct = [...activityEntries]; newAct[idx] = {...newAct[idx], activityName: e.target.value}; setActivityEntries(newAct); }} placeholder="e.g., Student Council" />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Role/Position</label>
                    <input type="text" style={{ ...formInputBase, ...viewModeField }} value={act.activityRole || act.role || ''} disabled={!editingSections.activities} onChange={(e) => { const newAct = [...activityEntries]; newAct[idx] = {...newAct[idx], activityRole: e.target.value}; setActivityEntries(newAct); }} placeholder="e.g., President" />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Duration</label>
                    <input type="text" style={{ ...formInputBase, ...viewModeField }} value={act.activityDuration || ''} disabled={!editingSections.activities} onChange={(e) => { const newAct = [...activityEntries]; newAct[idx] = {...newAct[idx], activityDuration: e.target.value}; setActivityEntries(newAct); }} placeholder="e.g., 2 years" />
                  </div>
                </div>
                <div style={{ marginTop: '15px' }}>
                  <label className="form-label">Description</label>
                  <textarea style={{ ...formInputBase, ...viewModeField, minHeight: '80px', width: '100%', fontFamily: 'inherit' }} value={act.activityDescription || act.description || ''} disabled={!editingSections.activities} onChange={(e) => { const newAct = [...activityEntries]; newAct[idx] = {...newAct[idx], activityDescription: e.target.value}; setActivityEntries(newAct); }} placeholder="Describe your involvement" />
                </div>
              </div>
            ))}
            {activityEntries.length === 0 && <div style={{ color: '#999', padding: '20px', textAlign: 'center' }}>No activities added</div>}
            {editingSections.activities && (
              <button onClick={() => setActivityEntries([...activityEntries, { activityName: '', activityRole: '', activityDuration: '', activityDescription: '' }])} style={{ background: 'linear-gradient(135deg, #f97316 0%, #0d9488 100%)', color: 'white', border: 'none', padding: '12px 24px', borderRadius: '8px', cursor: 'pointer', fontWeight: '600', fontSize: '14px', marginTop: '10px' }}>
                + Add Activity
              </button>
            )}
          </div>

          {/* Section 11: Certifications */}
          <div className="card" style={elevatedCardStyle} data-section="certifications">
            <div className="card-header" style={sectionCardHeaderStyle}>
              <h3 className="card-title"><FontAwesomeIcon icon={faCertificate} /> Certifications & Training</h3>
              <div className="card-header-actions" style={{ display: 'flex', gap: '8px' }}>
                {!editingSections.certifications ? (
                  <button onClick={() => toggleSectionEdit('certifications')} style={{ background: 'linear-gradient(135deg, #f97316 0%, #0d9488 100%)', color: 'white', border: 'none', padding: '8px 16px', borderRadius: '6px', fontSize: '14px', fontWeight: '600', cursor: 'pointer' }}>
                    <FontAwesomeIcon icon={faEdit} /> Edit
                  </button>
                ) : (
                  <>
                    <button onClick={() => saveSection('certifications')} disabled={saving} style={{ background: '#10b981', color: 'white', border: 'none', padding: '8px 16px', borderRadius: '6px', fontSize: '14px', fontWeight: '600' }}>
                      <FontAwesomeIcon icon={faSave} /> {saving ? 'Saving...' : 'Save'}
                    </button>
                    <button onClick={() => toggleSectionEdit('certifications')} style={{ background: '#dc2626', color: 'white', border: 'none', padding: '8px 16px', borderRadius: '6px', fontSize: '14px', fontWeight: '600' }}>
                      <FontAwesomeIcon icon={faTimes} /> Cancel
                    </button>
                  </>
                )}
              </div>
            </div>
            {certificationEntries.map((cert, idx) => (
              <div key={idx} style={{ padding: '20px', background: '#f9fafb', borderRadius: '12px', marginBottom: '15px', position: 'relative' }}>
                {editingSections.certifications && (
                  <button onClick={() => setCertificationEntries(certificationEntries.filter((_, i) => i !== idx))} style={{ position: 'absolute', top: '10px', right: '10px', background: '#dc2626', border: 'none', borderRadius: '50%', width: '30px', height: '30px', color: 'white', cursor: 'pointer' }}>×</button>
                )}
                <div className="profile-form-grid" style={formGridStyle('repeat(2, 1fr)')}>
                  <div className="form-group">
                    <label className="form-label">Certification Name</label>
                    <input type="text" style={{ ...formInputBase, ...viewModeField }} value={cert.certName || cert.name || ''} disabled={!editingSections.certifications} onChange={(e) => { const newCert = [...certificationEntries]; newCert[idx] = {...newCert[idx], certName: e.target.value}; setCertificationEntries(newCert); }} placeholder="e.g., Google Analytics" />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Issuing Organization</label>
                    <input type="text" style={{ ...formInputBase, ...viewModeField }} value={cert.certIssuer || cert.issuer || ''} disabled={!editingSections.certifications} onChange={(e) => { const newCert = [...certificationEntries]; newCert[idx] = {...newCert[idx], certIssuer: e.target.value}; setCertificationEntries(newCert); }} placeholder="e.g., Google" />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Issue Date</label>
                    <input type="month" style={{ ...formInputBase, ...viewModeField }} value={cert.certDate || cert.issueDate || ''} disabled={!editingSections.certifications} onChange={(e) => { const newCert = [...certificationEntries]; newCert[idx] = {...newCert[idx], certDate: e.target.value}; setCertificationEntries(newCert); }} />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Credential ID</label>
                    <input type="text" style={{ ...formInputBase, ...viewModeField }} value={cert.credentialId || ''} disabled={!editingSections.certifications} onChange={(e) => { const newCert = [...certificationEntries]; newCert[idx] = {...newCert[idx], credentialId: e.target.value}; setCertificationEntries(newCert); }} placeholder="Certificate number" />
                  </div>
                </div>
              </div>
            ))}
            {certificationEntries.length === 0 && <div style={{ color: '#999', padding: '20px', textAlign: 'center' }}>No certifications added</div>}
            {editingSections.certifications && (
              <button onClick={() => setCertificationEntries([...certificationEntries, { certName: '', certIssuer: '', certDate: '', credentialId: '' }])} style={{ background: 'linear-gradient(135deg, #f97316 0%, #0d9488 100%)', color: 'white', border: 'none', padding: '12px 24px', borderRadius: '8px', cursor: 'pointer', fontWeight: '600', fontSize: '14px', marginTop: '10px' }}>
                + Add Certification
              </button>
            )}
          </div>

          {/* Section 12: References */}
          <div className="card" style={elevatedCardStyle} data-section="references">
            <div className="card-header" style={sectionCardHeaderStyle}>
              <h3 className="card-title"><FontAwesomeIcon icon={faUserCheck} /> References</h3>
              <div className="card-header-actions" style={{ display: 'flex', gap: '8px' }}>
                {!editingSections.references ? (
                  <button onClick={() => toggleSectionEdit('references')} style={{ background: 'linear-gradient(135deg, #f97316 0%, #0d9488 100%)', color: 'white', border: 'none', padding: '8px 16px', borderRadius: '6px', fontSize: '14px', fontWeight: '600', cursor: 'pointer' }}>
                    <FontAwesomeIcon icon={faEdit} /> Edit
                  </button>
                ) : (
                  <>
                    <button onClick={() => saveSection('references')} disabled={saving} style={{ background: '#10b981', color: 'white', border: 'none', padding: '8px 16px', borderRadius: '6px', fontSize: '14px', fontWeight: '600' }}>
                      <FontAwesomeIcon icon={faSave} /> {saving ? 'Saving...' : 'Save'}
                    </button>
                    <button onClick={() => toggleSectionEdit('references')} style={{ background: '#dc2626', color: 'white', border: 'none', padding: '8px 16px', borderRadius: '6px', fontSize: '14px', fontWeight: '600' }}>
                      <FontAwesomeIcon icon={faTimes} /> Cancel
                    </button>
                  </>
                )}
              </div>
            </div>
            {referenceEntries.map((ref, idx) => (
              <div key={idx} style={{ padding: '20px', background: '#f9fafb', borderRadius: '12px', marginBottom: '15px', position: 'relative' }}>
                <div className="profile-form-grid" style={formGridStyle('repeat(2, 1fr)')}>
                  <div className="form-group">
                    <label className="form-label">Name</label>
                    <input type="text" style={{ ...formInputBase, ...viewModeField }} value={ref.referenceName || ref.name || ''} disabled={!editingSections.references} onChange={(e) => { const newRef = [...referenceEntries]; newRef[idx] = {...newRef[idx], referenceName: e.target.value}; setReferenceEntries(newRef); }} placeholder="Full name" />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Title/Position</label>
                    <input type="text" style={{ ...formInputBase, ...viewModeField }} value={ref.referenceTitle || ''} disabled={!editingSections.references} onChange={(e) => { const newRef = [...referenceEntries]; newRef[idx] = {...newRef[idx], referenceTitle: e.target.value}; setReferenceEntries(newRef); }} placeholder="e.g., Professor, Manager" />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Organization</label>
                    <input type="text" style={{ ...formInputBase, ...viewModeField }} value={ref.referenceOrg || ref.organization || ''} disabled={!editingSections.references} onChange={(e) => { const newRef = [...referenceEntries]; newRef[idx] = {...newRef[idx], referenceOrg: e.target.value}; setReferenceEntries(newRef); }} placeholder="University/Company" />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Relationship</label>
                    <input type="text" style={{ ...formInputBase, ...viewModeField }} value={ref.referenceRelationship || ref.relationship || ''} disabled={!editingSections.references} onChange={(e) => { const newRef = [...referenceEntries]; newRef[idx] = {...newRef[idx], referenceRelationship: e.target.value}; setReferenceEntries(newRef); }} placeholder="e.g., Professor & Academic Advisor" />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Email</label>
                    <input type="email" style={{ ...formInputBase, ...viewModeField }} value={ref.referenceEmail || ref.email || ''} disabled={!editingSections.references} onChange={(e) => { const newRef = [...referenceEntries]; newRef[idx] = {...newRef[idx], referenceEmail: e.target.value}; setReferenceEntries(newRef); }} placeholder="email@example.com" />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Phone</label>
                    <input type="tel" style={{ ...formInputBase, ...viewModeField }} value={ref.referencePhone || ref.phone || ''} disabled={!editingSections.references} onChange={(e) => { const newRef = [...referenceEntries]; newRef[idx] = {...newRef[idx], referencePhone: e.target.value}; setReferenceEntries(newRef); }} placeholder="+1 234 567 8900" />
                  </div>
                </div>
                {editingSections.references && (
                  <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '12px' }}>
                    <button
                      onClick={() => setReferenceEntries(referenceEntries.filter((_, i) => i !== idx))}
                      style={{
                        background: 'rgba(220,38,38,0.1)',
                        color: '#b91c1c',
                        border: '1px solid rgba(220,38,38,0.2)',
                        borderRadius: '8px',
                        padding: '8px 14px',
                        cursor: 'pointer',
                        fontWeight: '600',
                        fontSize: '13px',
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '6px'
                      }}
                    >
                      <FontAwesomeIcon icon={faTrash} />
                      Remove Reference
                    </button>
                  </div>
                )}
              </div>
            ))}
            {referenceEntries.length === 0 && <div style={{ color: '#999', padding: '20px', textAlign: 'center' }}>No references added</div>}
            {editingSections.references && (
              <button onClick={() => setReferenceEntries([...referenceEntries, { referenceName: '', referenceTitle: '', referenceOrg: '', referenceRelationship: '', referenceEmail: '', referencePhone: '' }])} style={{ background: 'linear-gradient(135deg, #f97316 0%, #0d9488 100%)', color: 'white', border: 'none', padding: '12px 24px', borderRadius: '8px', cursor: 'pointer', fontWeight: '600', fontSize: '14px', marginTop: '10px' }}>
                + Add Reference
              </button>
            )}
          </div>

          {/* Section 13: Online Links */}
          <div className="card" style={elevatedCardStyle} data-section="links">
            <div className="card-header" style={sectionCardHeaderStyle}>
              <h3 className="card-title"><FontAwesomeIcon icon={faLink} /> Online Presence & Portfolio</h3>
              <div className="card-header-actions" style={{ display: 'flex', gap: '8px' }}>
                {!editingSections.links ? (
                  <button onClick={() => toggleSectionEdit('links')} style={{ background: 'linear-gradient(135deg, #f97316 0%, #0d9488 100%)', color: 'white', border: 'none', padding: '8px 16px', borderRadius: '6px', fontSize: '14px', fontWeight: '600', cursor: 'pointer' }}>
                    <FontAwesomeIcon icon={faEdit} /> Edit
                  </button>
                ) : (
                  <>
                    <button onClick={() => saveSection('links')} disabled={saving} style={{ background: '#10b981', color: 'white', border: 'none', padding: '8px 16px', borderRadius: '6px', fontSize: '14px', fontWeight: '600' }}>
                      <FontAwesomeIcon icon={faSave} /> {saving ? 'Saving...' : 'Save'}
                    </button>
                    <button onClick={() => toggleSectionEdit('links')} style={{ background: '#dc2626', color: 'white', border: 'none', padding: '8px 16px', borderRadius: '6px', fontSize: '14px', fontWeight: '600' }}>
                      <FontAwesomeIcon icon={faTimes} /> Cancel
                    </button>
                  </>
                )}
              </div>
            </div>
            {editingSections.links && (
              <div style={{ padding: '20px', background: '#f0fdf4', borderRadius: '12px', marginBottom: '20px', border: '2px dashed #0d9488' }}>
                <div className="profile-form-grid" style={formGridStyle('1fr 2fr auto', { gap: '15px', alignItems: 'end' })}>
                  <div className="form-group">
                    <label className="form-label">Link Type</label>
                    <select 
                      style={formInputBase}
                      value={linkInput.type}
                      onChange={(e) => setLinkInput(prev => ({ ...prev, type: e.target.value }))}
                    >
                      <option value="">Select type</option>
                      <option value="LinkedIn">LinkedIn</option>
                      <option value="GitHub">GitHub</option>
                      <option value="Personal">Personal Website/Portfolio</option>
                      <option value="Twitter/X">Twitter/X</option>
                      <option value="Behance">Behance</option>
                      <option value="Dribbble">Dribbble</option>
                      <option value="Medium">Medium</option>
                      <option value="Stack Overflow">Stack Overflow</option>
                      <option value="Other">Other Professional</option>
                    </select>
                  </div>
                  <div className="form-group">
                    <label className="form-label">URL</label>
                    <input 
                      type="url" 
                      style={formInputBase}
                      placeholder="https://example.com/yourprofile"
                      value={linkInput.url}
                      onChange={(e) => setLinkInput(prev => ({ ...prev, url: e.target.value }))}
                    />
                  </div>
                  <button 
                    onClick={() => {
                      if (linkInput.type && linkInput.url) {
                        setProfessionalLinks([...professionalLinks, linkInput]);
                        setLinkInput({ type: '', url: '' });
                      } else {
                        alert('Please select a link type and enter a URL');
                      }
                    }}
                    style={{ 
                      background: 'linear-gradient(135deg, #f97316 0%, #0d9488 100%)', 
                      color: 'white', 
                      border: 'none', 
                      padding: '12px 24px', 
                      borderRadius: '8px', 
                      cursor: 'pointer', 
                      fontWeight: '600', 
                      fontSize: '14px',
                      height: 'fit-content',
                      marginBottom: '2px'
                    }}
                  >
                    + Add
                  </button>
                </div>
              </div>
            )}
            
            {professionalLinks.map((link, idx) => (
              <div key={idx} style={{ padding: '15px', background: '#f9fafb', borderRadius: '12px', marginBottom: '10px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: '600', color: '#0d9488', marginBottom: '4px', fontSize: '14px' }}>{link.type}</div>
                  {editingSections.links ? (
                    <input 
                      type="url"
                      style={{ ...formInputBase, marginTop: '5px' }}
                      value={link.url}
                      onChange={(e) => {
                        const updated = [...professionalLinks];
                        updated[idx] = { ...updated[idx], url: e.target.value };
                        setProfessionalLinks(updated);
                      }}
                      placeholder="https://example.com/yourprofile"
                    />
                  ) : (
                  <a href={link.url} target="_blank" rel="noopener noreferrer" style={{ color: '#666', fontSize: '13px', wordBreak: 'break-all', textDecoration: 'none' }}>{link.url}</a>
                  )}
                </div>
                {editingSections.links && (
                  <button onClick={() => setProfessionalLinks(professionalLinks.filter((_, i) => i !== idx))} style={{ background: 'none', border: 'none', color: '#dc2626', cursor: 'pointer', fontSize: '20px', marginLeft: '10px' }}>×</button>
                )}
              </div>
            ))}
            {professionalLinks.length === 0 && <div style={{ color: '#999', padding: '20px', textAlign: 'center' }}>No links added</div>}
          </div>

          {/* Section 14: Internship Preferences */}
          <div className="card" style={elevatedCardStyle} data-section="preferences">
            <div className="card-header" style={sectionCardHeaderStyle}>
              <h3 className="card-title"><FontAwesomeIcon icon={faSlidersH} /> Internship Preferences & Availability</h3>
              <div className="card-header-actions" style={{ display: 'flex', gap: '8px' }}>
                {!editingSections.preferences ? (
                  <button onClick={() => toggleSectionEdit('preferences')} style={{ background: 'linear-gradient(135deg, #f97316 0%, #0d9488 100%)', color: 'white', border: 'none', padding: '8px 16px', borderRadius: '6px', fontSize: '14px', fontWeight: '600', cursor: 'pointer' }}>
                    <FontAwesomeIcon icon={faEdit} /> Edit
                  </button>
                ) : (
                  <>
                    <button onClick={() => saveSection('preferences')} disabled={saving} style={{ background: '#10b981', color: 'white', border: 'none', padding: '8px 16px', borderRadius: '6px', fontSize: '14px', fontWeight: '600' }}>
                      <FontAwesomeIcon icon={faSave} /> {saving ? 'Saving...' : 'Save'}
                    </button>
                    <button onClick={() => toggleSectionEdit('preferences')} style={{ background: '#dc2626', color: 'white', border: 'none', padding: '8px 16px', borderRadius: '6px', fontSize: '14px', fontWeight: '600' }}>
                      <FontAwesomeIcon icon={faTimes} /> Cancel
                    </button>
                  </>
                )}
              </div>
            </div>
            <div className="profile-form-grid" style={formGridStyle('repeat(2, 1fr)')}>
              <div className="form-group">
                <label className="form-label">Internship Duration Preference</label>
                <select style={{ ...formInputBase, ...viewModeField }} value={profileForm.internshipDuration || ''} disabled={!editingSections.preferences} onChange={(e) => setProfileForm(p => ({ ...p, internshipDuration: e.target.value }))}>
                  <option value="">Select preferred duration</option>
                  <option value="1-2-months">1-2 months</option>
                  <option value="3-months">3 months</option>
                  <option value="4-6-months">4-6 months</option>
                  <option value="6-12-months">6-12 months</option>
                  <option value="12-months-plus">12+ months</option>
                  <option value="flexible">Flexible</option>
                  <option value="other">Other</option>
                </select>
              </div>
              <div className="form-group">
                <label className="form-label">Availability to Start</label>
                <select style={{ ...formInputBase, ...viewModeField }} value={profileForm.availability || ''} disabled={!editingSections.preferences} onChange={(e) => setProfileForm(p => ({ ...p, availability: e.target.value }))}>
                  <option value="">Select availability</option>
                  <option value="immediate">Immediately Available</option>
                  <option value="1-week">Within 1 week</option>
                  <option value="2-weeks">Within 2 weeks</option>
                  <option value="1-month">Within 1 month</option>
                  <option value="2-months">Within 2 months</option>
                  <option value="3-months">Within 3 months</option>
                  <option value="summer">Summer Break</option>
                  <option value="semester">Next Semester</option>
                  <option value="specific-date">Specific Date (add in comments)</option>
                  <option value="other">Other</option>
                </select>
              </div>
              <div className="form-group">
                <label className="form-label">Start From: Month Year</label>
                <input 
                  type="text" 
                  style={{ ...formInputBase, ...viewModeField }} 
                  value={profileForm.internshipStartMonthYear || ''} 
                  disabled={!editingSections.preferences} 
                  onChange={(e) => setProfileForm(p => ({ ...p, internshipStartMonthYear: e.target.value }))} 
                  placeholder="e.g., January 2025" 
                />
              </div>
              <div className="form-group">
                <label className="form-label">Expected Stipend</label>
                <input type="text" style={{ ...formInputBase, ...viewModeField }} value={profileForm.expectedStipend || ''} disabled={!editingSections.preferences} onChange={(e) => setProfileForm(p => ({ ...p, expectedStipend: e.target.value }))} placeholder="e.g., USD 500/month" />
              </div>
              <div className="form-group">
                <label className="form-label">Currency Preference</label>
                <select style={{ ...formInputBase, ...viewModeField }} value={profileForm.currencyPreference || 'USD'} disabled={!editingSections.preferences} onChange={(e) => setProfileForm(p => ({ ...p, currencyPreference: e.target.value }))}>
                  <option value="USD">USD - US Dollar</option>
                  <option value="EUR">EUR - Euro</option>
                  <option value="GBP">GBP - British Pound</option>
                  <option value="KES">KES - Kenyan Shilling</option>
                  <option value="ZAR">ZAR - South African Rand</option>
                  <option value="NGN">NGN - Nigerian Naira</option>
                  <option value="GHS">GHS - Ghanaian Cedi</option>
                  <option value="UGX">UGX - Ugandan Shilling</option>
                  <option value="TZS">TZS - Tanzanian Shilling</option>
                  <option value="AED">AED - UAE Dirham</option>
                  <option value="CAD">CAD - Canadian Dollar</option>
                  <option value="AUD">AUD - Australian Dollar</option>
                  <option value="INR">INR - Indian Rupee</option>
                  <option value="other">Other</option>
                </select>
              </div>
              <div className="form-group">
                <label className="form-label">Willing to Accept Unpaid Internship?</label>
                <select style={{ ...formInputBase, ...viewModeField }} value={profileForm.unpaidWilling || ''} disabled={!editingSections.preferences} onChange={(e) => setProfileForm(p => ({ ...p, unpaidWilling: e.target.value }))}>
                  <option value="">Select...</option>
                  <option value="yes">Yes</option>
                  <option value="prefer-paid">Prefer paid but open</option>
                  <option value="no">No, paid only</option>
                </select>
              </div>
              <div className="form-group">
                <label className="form-label">Academic Credit Required?</label>
                <select style={{ ...formInputBase, ...viewModeField }} value={profileForm.academicCredit || ''} disabled={!editingSections.preferences} onChange={(e) => setProfileForm(p => ({ ...p, academicCredit: e.target.value }))}>
                  <option value="">Select...</option>
                  <option value="yes">Yes, required</option>
                  <option value="preferred">Preferred but not required</option>
                  <option value="no">No</option>
                </select>
              </div>
            </div>
          </div>

          {/* Section 15: Additional Information */}
          <div className="card" style={elevatedCardStyle} data-section="additional">
            <div className="card-header" style={sectionCardHeaderStyle}>
              <h3 className="card-title"><FontAwesomeIcon icon={faInfoCircle} /> Additional Information</h3>
              <div className="card-header-actions" style={{ display: 'flex', gap: '8px' }}>
                {!editingSections.additional ? (
                  <button onClick={() => toggleSectionEdit('additional')} style={{ background: 'linear-gradient(135deg, #f97316 0%, #0d9488 100%)', color: 'white', border: 'none', padding: '8px 16px', borderRadius: '6px', fontSize: '14px', fontWeight: '600', cursor: 'pointer' }}>
                    <FontAwesomeIcon icon={faEdit} /> Edit
                  </button>
                ) : (
                  <>
                    <button onClick={() => saveSection('additional')} disabled={saving} style={{ background: '#10b981', color: 'white', border: 'none', padding: '8px 16px', borderRadius: '6px', fontSize: '14px', fontWeight: '600' }}>
                      <FontAwesomeIcon icon={faSave} /> {saving ? 'Saving...' : 'Save'}
                    </button>
                    <button onClick={() => toggleSectionEdit('additional')} style={{ background: '#dc2626', color: 'white', border: 'none', padding: '8px 16px', borderRadius: '6px', fontSize: '14px', fontWeight: '600' }}>
                      <FontAwesomeIcon icon={faTimes} /> Cancel
                    </button>
                  </>
                )}
              </div>
            </div>
            <div style={{ marginBottom: '20px' }}>
              <label className="form-label">Hobbies & Interests</label>
              <textarea style={{ ...formInputBase, ...viewModeField, minHeight: '100px', width: '100%', fontFamily: 'inherit' }} value={profileForm.hobbies || ''} disabled={!editingSections.additional} onChange={(e) => setProfileForm(p => ({ ...p, hobbies: e.target.value }))} placeholder="Share your interests outside academics" />
            </div>
            <div style={{ marginBottom: '20px' }}>
              <label className="form-label">Why Internship?</label>
              <textarea style={{ ...formInputBase, ...viewModeField, minHeight: '100px', width: '100%', fontFamily: 'inherit' }} value={profileForm.whyInternship || ''} disabled={!editingSections.additional} onChange={(e) => setProfileForm(p => ({ ...p, whyInternship: e.target.value }))} placeholder="Share your motivation" />
            </div>
            <div>
              <label className="form-label">Additional Comments</label>
              <textarea style={{ ...formInputBase, ...viewModeField, minHeight: '100px', width: '100%', fontFamily: 'inherit' }} value={profileForm.additionalComments || ''} disabled={!editingSections.additional} onChange={(e) => setProfileForm(p => ({ ...p, additionalComments: e.target.value }))} placeholder="Any other information" />
            </div>
            
            <div style={{ marginBottom: '20px' }}>
              <label className="form-label">Would you like to participate in any volunteering?</label>
              <select 
                style={{ ...formInputBase, ...viewModeField }} 
                value={profileForm.participateInVolunteering || ''} 
                disabled={!editingSections.additional} 
                onChange={(e) => setProfileForm(p => ({ ...p, participateInVolunteering: e.target.value }))}
              >
                <option value="">Select an option</option>
                <option value="yes">Yes</option>
                <option value="no">No</option>
              </select>
            </div>

            {profileForm.participateInVolunteering === 'yes' && (
              <div>
                <label className="form-label">Volunteering Details</label>
                <textarea 
                  style={{ ...formInputBase, ...viewModeField, minHeight: '100px', width: '100%', fontFamily: 'inherit' }} 
                  value={profileForm.volunteeringDetails || ''} 
                  disabled={!editingSections.additional} 
                  onChange={(e) => setProfileForm(p => ({ ...p, volunteeringDetails: e.target.value }))} 
                  placeholder="Please describe the volunteering activities you'd like to participate in"
                />
              </div>
            )}
          </div>

          {/* Account Deletion Section - Only show for own profile */}
          {!isViewingOtherProfile && (
            <div style={{ 
              marginTop: '3rem', 
              padding: '2rem', 
              borderTop: '2px solid #e5e7eb',
              background: '#fef2f2',
              borderRadius: '8px'
            }}>
              <h3 style={{ color: '#dc2626', marginBottom: '1rem', display: 'flex', alignItems: 'center', gap: '10px' }}>
                <FontAwesomeIcon icon={faTrash} /> Danger Zone
              </h3>
              <p style={{ color: '#6b7280', marginBottom: '1.5rem' }}>
                Once you delete your account, there is no going back. Please be certain.
              </p>
              <button
                onClick={() => setShowDeleteModal(true)}
                style={{
                  background: '#dc2626',
                  color: 'white',
                  border: 'none',
                  padding: '12px 24px',
                  borderRadius: '6px',
                  fontSize: '14px',
                  fontWeight: '600',
                  cursor: 'pointer',
                  transition: 'all 0.3s ease',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px'
                }}
                onMouseOver={(e) => e.target.style.background = '#b91c1c'}
                onMouseOut={(e) => e.target.style.background = '#dc2626'}
              >
                <FontAwesomeIcon icon={faTrash} /> Delete Account
              </button>
        </div>
          )}

      </div>
      </div>

      <AccountDeletionModal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        onDelete={async (deletionData) => {
          try {
            const token = localStorage.getItem('token');
            const response = await axios.delete(
              buildApiUrl('/api/user/settings/delete-account'),
              {
                headers: { Authorization: `Bearer ${token}` },
                data: deletionData
              }
            );

            if (response.status === 200) {
              // Clear all local storage
              localStorage.clear();
              // Logout user
              if (logout) {
                logout();
              }
              // Navigate to home
              navigate('/');
              alert('Your account has been permanently deleted.');
            }
          } catch (error) {
            console.error('Error deleting account:', error);
            throw error;
          }
        }}
        accountType="intern"
      />
      {showResumeUpload && (
        <ResumeUpload
          onResumeUploaded={handleResumeUploaded}
          onClose={() => setShowResumeUpload(false)}
        />
      )}
    </div>
  );
};

export default InternMyProfile;
